/**
 * UniversalVoiceWizard.jsx
 * 
 * Universal Voice Agent Configuration Wizard (Phase 4 - Public Beta)
 * 
 * This is NOT a "Booking Wizard" - it's a Universal Voice Agent Wizard Framework.
 * It adapts dynamically based on agent subtype (booking, receptionist, general voice).
 * 
 * WIZARD TRIGGER RULES:
 * - Auto-launches after Install if capability_profile requires wizard
 * - Agent is created in DRAFT first
 * - Wizard writes structured config into agent
 * - User may exit to Advanced Editor at any time
 * - Activation remains blocked until Phase 3 gates pass
 * 
 * MODULAR STEPS:
 * 1️⃣ Voice Mode - Live S2S, Voice input only, Voice output only
 * 2️⃣ Business / Persona - Name, description, greeting message
 * 3️⃣ Call Behaviour - Interruptibility, max duration, fallback
 * 4️⃣ Booking / Data Collection (conditional) - Required fields, Special Notes
 * 5️⃣ Services Catalog (conditional) - Duration enforcement
 * 6️⃣ Calendar (conditional) - Google Calendar connection
 * 7️⃣ SMS (Twilio) - Confirmation, reminders, templates
 * 8️⃣ Voice GearSix - Accent/locale, Voice ID, STT/TTS providers
 * 9️⃣ Twilio Setup - Credential validation, phone number
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import VoiceTestMode from './VoiceTestMode';

import {
  ArrowSquareOut,
  Bell,
  Buildings,
  Calendar,
  CaretLeft,
  CaretRight,
  ChatText,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  Copy,
  File,
  FileText,
  GearSix,
  Globe,
  Lightning,
  Lock,
  Microphone,
  PaperPlaneRight,
  Phone,
  Plus,
  SpeakerHigh,
  Star,
  Trash,
  UploadSimple,
  Warning,
  WarningCircle,
  Waveform,
  WebhooksLogo,
  X
} from '@phosphor-icons/react';
// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

// Voice mode options (Step 1)
const VOICE_MODES = [
  { 
    id: 'live_s2s', 
    label: 'Live Voice (S2S)', 
    description: 'Real-time conversational voice agent - answers and makes calls',
    icon: Microphone,
    color: 'teal',
    requiresTwilio: true,
    requiresCalendar: false
  },
  { 
    id: 'voice_input', 
    label: 'Voice Input Only', 
    description: 'Consumes audio/transcripts but does not speak back',
    icon: Waveform,
    color: 'teal',
    requiresTwilio: false,
    requiresCalendar: false
  },
  { 
    id: 'voice_output', 
    label: 'Voice Output Only', 
    description: 'Converts text responses to speech (TTS only)',
    icon: SpeakerHigh,
    color: 'blue',
    requiresTwilio: false,
    requiresCalendar: false
  }
];

// Accent/locale options
const ACCENT_LOCALES = [
  { value: 'en-US', label: 'English (US)', flag: '🇺🇸' },
  { value: 'en-GB', label: 'English (UK)', flag: '🇬🇧' },
  { value: 'en-AU', label: 'English (Australia)', flag: '🇦🇺' },
  { value: 'en-NZ', label: 'English (New Zealand)', flag: '🇳🇿' },
  { value: 'en-IN', label: 'English (India)', flag: '🇮🇳' },
  { value: 'es-ES', label: 'Spanish (Spain)', flag: '🇪🇸' },
  { value: 'es-MX', label: 'Spanish (Mexico)', flag: '🇲🇽' },
  { value: 'fr-FR', label: 'French (France)', flag: '🇫🇷' },
  { value: 'de-DE', label: 'German', flag: '🇩🇪' },
  { value: 'it-IT', label: 'Italian', flag: '🇮🇹' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: '🇧🇷' },
  { value: 'ja-JP', label: 'Japanese', flag: '🇯🇵' },
  { value: 'zh-CN', label: 'Chinese (Mandarin)', flag: '🇨🇳' },
];

// Timezone options
const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEDT)' },
  { value: 'Australia/Brisbane', label: 'Australia/Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
  { value: 'America/New_York', label: 'America/New York (EST)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (PST)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
];

const WEEKDAYS = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

// Special Notes destination options (Phase 4 fix)
const SPECIAL_NOTES_DESTINATIONS = [
  { value: 'calendar_note', label: 'Calendar Event Notes', description: 'Save to calendar event description' },
  { value: 'crm_note', label: 'CRM Note', description: 'Save as a CRM record/note' },
  { value: 'structured_output', label: 'Structured Output', description: 'Include in webhook payload' },
];

// Interruptibility options
const INTERRUPTIBILITY_OPTIONS = [
  { value: 'barge-in', label: 'Allow Interruption (Barge-in)', description: 'Caller can interrupt the agent at any time' },
  { value: 'wait', label: 'Wait for Completion', description: 'Agent finishes speaking before processing new input' },
  { value: 'smart', label: 'Smart Detection', description: 'Agent detects natural pauses and questions' },
];

// End of call action options
const END_OF_CALL_ACTIONS = [
  { id: 'summarize', label: 'Generate Summary', description: 'Create a summary of the call' },
  { id: 'trigger_workflow', label: 'Trigger Workflow', description: 'Start an automated workflow' },
  { id: 'send_sms', label: 'Send SMS', description: 'Send a confirmation/follow-up SMS' },
  { id: 'send_email', label: 'Send Email', description: 'Send a confirmation email' },
];

// Available booking info options
// Phase C: Removed 'notes' - Special Notes has dedicated configuration section below
const BOOKING_INFO_OPTIONS = [
  { id: 'name', label: 'Full Name', description: 'Customer\'s full name', required: true },
  { id: 'phone', label: 'Phone Number', description: 'Contact phone number', required: true },
  { id: 'email', label: 'Email Address', description: 'Email for confirmation' },
  { id: 'address', label: 'Address', description: 'Home or service address' },
  { id: 'payment', label: 'Payment Method', description: 'Preferred payment method' },
];

// Default SMS templates
const DEFAULT_SMS_TEMPLATES = {
  confirmation: "Hi {name}, your appointment for {service} on {date} at {time} is confirmed. Reply CANCEL to cancel.",
  reminder: "Reminder: Your appointment for {service} is tomorrow at {time}. See you at {business_name}!"
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIZARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const UniversalVoiceWizard = ({ agentIdProp, isModal, onClose: onModalClose }) => {
  const navigate = useNavigate();
  const { agentId: routeAgentId } = useParams();
  const [searchParams] = useSearchParams();
  const agentId = agentIdProp || routeAgentId;
  const { activeOrganization } = useOrganization();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [advancing, setAdvancing] = useState(false); // Prevent double-clicks
  const [error, setError] = useState(null);
  const [agent, setAgent] = useState(null);
  const [isBookingAgent, setIsBookingAgent] = useState(false);
  
  // Phase G: Cancel confirmation modal
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 1: Voice Mode Selection
  // ═══════════════════════════════════════════════════════════════════════════
  const [voiceMode, setVoiceMode] = useState('live_s2s');
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2: Business / Persona
  // ═══════════════════════════════════════════════════════════════════════════
  const [personaName, setPersonaName] = useState('');
  const [personaDescription, setPersonaDescription] = useState('');
  const [greetingMessage, setGreetingMessage] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 3: Call Behaviour
  // ═══════════════════════════════════════════════════════════════════════════
  const [interruptibility, setInterruptibility] = useState('smart');
  const [maxCallDuration, setMaxCallDuration] = useState(20); // minutes
  const [silenceTimeout, setSilenceTimeout] = useState(7); // seconds
  const [fallbackBehavior, setFallbackBehavior] = useState('');
  const [endOfCallActions, setEndOfCallActions] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 4: Booking / Data Collection (conditional)
  // ═══════════════════════════════════════════════════════════════════════════
  const [requiredBookingInfo, setRequiredBookingInfo] = useState(['name', 'phone']);
  const [specialNotesEnabled, setSpecialNotesEnabled] = useState(false);
  const [specialNotesDestination, setSpecialNotesDestination] = useState('calendar_note');
  const [specialNotesPrompt, setSpecialNotesPrompt] = useState('');
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 5: Services Catalog (conditional)
  // ═══════════════════════════════════════════════════════════════════════════
  const [services, setServices] = useState([
    { service_name: '', duration_minutes: 30, price: '', description: '' }
  ]);
  const [timezone, setTimezone] = useState('Australia/Sydney');
  // Per-day working hours (replaces single openingTime/closingTime)
  const [daySchedule, setDaySchedule] = useState({
    monday: { enabled: true, opening: '09:00', closing: '17:00' },
    tuesday: { enabled: true, opening: '09:00', closing: '17:00' },
    wednesday: { enabled: true, opening: '09:00', closing: '17:00' },
    thursday: { enabled: true, opening: '09:00', closing: '17:00' },
    friday: { enabled: true, opening: '09:00', closing: '17:00' },
    saturday: { enabled: false, opening: '09:00', closing: '17:00' },
    sunday: { enabled: false, opening: '09:00', closing: '17:00' },
  });
  // Legacy fields for backward compatibility
  const workingDays = Object.entries(daySchedule).filter(([_, v]) => v.enabled).map(([k, _]) => k);
  const openingTime = daySchedule.monday?.opening || '09:00';
  const closingTime = daySchedule.monday?.closing || '17:00';
  const [bufferMinutes, setBufferMinutes] = useState(0);
  const [minNoticeMinutes, setMinNoticeMinutes] = useState(60);
  const [bookingHorizonDays, setBookingHorizonDays] = useState(60);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 6: Calendar (conditional)
  // ═══════════════════════════════════════════════════════════════════════════
  const [calendarProvider, setCalendarProvider] = useState('google');
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [calendarOAuthConfigured, setCalendarOAuthConfigured] = useState(null); // null = loading, true/false = status
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 7: SMS Configuration (NEW - Phase 4)
  // ═══════════════════════════════════════════════════════════════════════════
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsConfirmationEnabled, setSmsConfirmationEnabled] = useState(true);
  const [smsConfirmationTemplate, setSmsConfirmationTemplate] = useState(DEFAULT_SMS_TEMPLATES.confirmation);
  const [smsReminderEnabled, setSmsReminderEnabled] = useState(false);
  const [smsReminderHours, setSmsReminderHours] = useState(24);
  const [smsReminderTemplate, setSmsReminderTemplate] = useState(DEFAULT_SMS_TEMPLATES.reminder);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 8: Voice GearSix (Phase D: True S2S Support)
  // ═══════════════════════════════════════════════════════════════════════════
  const [accentLocale, setAccentLocale] = useState('en-AU');
  const [voiceConversationMode, setVoiceConversationMode] = useState('realtime_s2s'); // 'realtime_s2s' or 'stt_tts'
  const [realtimeProvider, setRealtimeProvider] = useState('openai_realtime'); // For S2S mode
  const [sttProvider, setSttProvider] = useState('openai_whisper'); // For STT+TTS mode
  const [ttsProvider, setTtsProvider] = useState('openai_tts'); // For STT+TTS mode
  const [ttsVoiceId, setTtsVoiceId] = useState('');
  const [availableVoices, setAvailableVoices] = useState([]);
  // S2S-specific settings (moved to STEP 8 section - silenceTimeout already declared above)
  const [bargeInEnabled, setBargeInEnabled] = useState(true);
  const [realtimeVoice, setRealtimeVoice] = useState('alloy');
  
  // ═══════════════════════════════════════════════════════════════════════════
  // BOOKING PROVIDER SELECTION (Square, Calendly, Google, Microsoft, WebhooksLogo)
  // ═══════════════════════════════════════════════════════════════════════════
  const [bookingProvider, setBookingProvider] = useState('google'); // 'square', 'calendly', 'google', 'microsoft', 'webhook'
  
  // WebhooksLogo Booking Configuration
  const [webhookBookingUrl, setWebhookBookingUrl] = useState('');
  const [webhookBookingHeaders, setWebhookBookingHeaders] = useState([
    { key: '', value: '' }
  ]);
  const [webhookBookingMethod, setWebhookBookingMethod] = useState('POST');
  const [webhookBookingTestResult, setWebhookBookingTestResult] = useState(null);
  const [testingWebhookBooking, setTestingWebhookBooking] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 9: Telephony Provider Setup (Twilio or Telnyx)
  // ═══════════════════════════════════════════════════════════════════════════
  const [telephonyProvider, setTelephonyProvider] = useState('twilio'); // 'twilio' or 'telnyx'
  
  // Twilio state
  const [twilioAccountSid, setTwilioAccountSid] = useState('');
  const [twilioAuthToken, setTwilioAuthToken] = useState('');
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState('');
  const [twilioValidated, setTwilioValidated] = useState(false);
  const [editingTwilioCreds, setEditingTwilioCreds] = useState(false);
  const [validatingTwilio, setValidatingTwilio] = useState(false);
  const [hasExistingTwilioToken, setHasExistingTwilioToken] = useState(false);
  
  // Telnyx state
  const [telnyxApiKey, setTelnyxApiKey] = useState('');
  const [telnyxPhoneNumber, setTelnyxPhoneNumber] = useState('');
  const [telnyxValidated, setTelnyxValidated] = useState(false);
  const [validatingTelnyx, setValidatingTelnyx] = useState(false);
  const [hasExistingTelnyxKey, setHasExistingTelnyxKey] = useState(false);
  
  // Training Data / Knowledge Base
  const [trainingFiles, setTrainingFiles] = useState([]);
  const [uploadingTrainingData, setUploadingTrainingData] = useState(false);
  const [customKnowledge, setCustomKnowledge] = useState(''); // Text-based knowledge
  
  const [webhookUrls, setWebhookUrls] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  
  // Voice Test Mode
  const [showVoiceTest, setShowVoiceTest] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════════
  // BOOKING PROVIDER OPTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  const BOOKING_PROVIDERS = [
    {
      id: 'square',
      name: 'Square',
      description: 'Recommended for service businesses (salons, trades, appointments)',
      recommended: true,
      category: 'recommended',
      color: 'bg-black'
    },
    {
      id: 'calendly',
      name: 'Calendly',
      description: 'Recommended for professionals (consultants, coaches, advisors)',
      recommended: true,
      category: 'recommended',
      color: 'bg-blue-600'
    },
    {
      id: 'google',
      name: 'Google Calendar',
      description: 'Connect your Google account',
      recommended: false,
      category: 'advanced',
      color: 'bg-white'
    },
    {
      id: 'microsoft',
      name: 'Microsoft 365 Calendar',
      description: 'Connect your Microsoft 365 account',
      recommended: false,
      category: 'advanced',
      color: 'bg-[#0078D4]'
    },
    {
      id: 'webhook',
      name: 'Custom / Webhook',
      description: 'Connect to any booking system via webhook',
      recommended: false,
      category: 'advanced',
      color: 'bg-[#00E5C0]'
    }
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // DYNAMIC STEPS based on voice mode and agent type
  // Wizard steps are conditional based on capability_profile/voiceMode:
  // - live_s2s: Full flow (telephony, call behavior, calendar, SMS)
  // - voice_input: STT focus, no telephony or call behavior
  // - voice_output: TTS focus, no telephony or call behavior
  // ═══════════════════════════════════════════════════════════════════════════
  const getSteps = useCallback(() => {
    const baseSteps = [
      { id: 'voice_mode', title: 'Voice Mode', icon: Microphone, required: true },
      { id: 'persona', title: 'Persona & Greeting', icon: Buildings, required: true },
      { id: 'knowledge_base', title: 'Knowledge Base', icon: FileText, required: false },
    ];
    
    // Call Behavior ONLY for Live S2S (not for input-only or output-only)
    if (voiceMode === 'live_s2s') {
      baseSteps.push({ id: 'call_behavior', title: 'Call Behaviour', icon: GearSix, required: true });
    }
    
    // Add booking-specific steps ONLY for Live S2S booking agents
    if (isBookingAgent && voiceMode === 'live_s2s') {
      baseSteps.push({ id: 'booking_provider', title: 'Booking Provider', icon: Calendar, required: true });
      baseSteps.push({ id: 'data_collection', title: 'Data Collection', icon: Clock, required: true });
      baseSteps.push({ id: 'services', title: 'Services Catalog', icon: Clock, required: true });
      // Show calendar step for all calendar-based booking providers
      if (bookingProvider === 'google' || bookingProvider === 'microsoft' || bookingProvider === 'calendly' || bookingProvider === 'square') {
        baseSteps.push({ id: 'calendar', title: 'Calendar', icon: Calendar, required: true });
      }
    }
    
    // SMS step ONLY for Live S2S
    if (voiceMode === 'live_s2s') {
      baseSteps.push({ id: 'sms', title: 'SMS Configuration', icon: Bell, required: false });
    }
    
    // Voice settings - different focus based on mode
    baseSteps.push({ id: 'voice_settings', title: 'Voice Settings', icon: SpeakerHigh, required: true });
    
    // Telephony ONLY for Live S2S (not for input-only or output-only)
    if (voiceMode === 'live_s2s') {
      baseSteps.push({ id: 'twilio', title: 'Telephony Setup', icon: Phone, required: true });
    }
    
    // Review step
    baseSteps.push({ id: 'review', title: 'Review & Test', icon: Check, required: true });
    
    return baseSteps;
  }, [voiceMode, isBookingAgent, bookingProvider]);
  
  const steps = getSteps();
  
  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE A: Voice Mode Lock Detection
  // Voice mode should be locked when:
  // 1. Agent was installed from template with voice_mode in locked fields
  // 2. Agent was created with a specific voice capability_profile (wizard-first flow)
  // ═══════════════════════════════════════════════════════════════════════════
  const capabilityProfile = agent?.capability_profile;
  
  // Lock voice mode if capability_profile is a specific voice type
  // This ensures agents created via "wizard-first" flow have their voice mode locked
  const isCapabilityProfileVoiceAgent = capabilityProfile && [
    'live_voice_s2s_agent',
    'voice_input_agent', 
    'voice_output_tts_agent'
  ].includes(capabilityProfile);
  
  const isVoiceModeLocked = isCapabilityProfileVoiceAgent || 
                            (agent?.installed_from_template && 
                             agent?.wizard_locked_fields?.includes('voice_mode'));
  const installedFromTemplate = agent?.installed_from_template || false;

  // ═══════════════════════════════════════════════════════════════════════════
  // LOAD AGENT
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (agentId && agentId !== 'create' && activeOrganization) {
      loadAgent();
      loadOrgTwilioCredentials(); // Load org-level Twilio credentials
    } else {
      setLoading(false);
    }
  }, [agentId, activeOrganization]);

  // Load available workflows for the organization
  useEffect(() => {
    const fetchWorkflows = async () => {
      if (!activeOrganization?.organization_id) return;
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/workflows?limit=50`,
          { credentials: 'include' },
          activeOrganization.organization_id
        );
        if (response.ok) {
          const data = await response.json();
          setAvailableWorkflows(data.workflows || []);
        }
      } catch (error) {
        console.error('Error fetching workflows:', error);
      }
    };
    fetchWorkflows();
  }, [activeOrganization]);

  // Load existing Twilio credentials from organization's tenant_credentials
  const loadOrgTwilioCredentials = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/integrations/twilio/status`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.connected && data.phone_number) {
          // Twilio is already configured for this org
          setTwilioPhoneNumber(data.phone_number);
          setTwilioValidated(true);
          // Account SID and Auth Token are not returned for security, but we know it's valid
          if (data.account_sid) {
            setTwilioAccountSid(data.account_sid);
          }
        }
      }
    } catch (err) {
      console.error('Error loading Twilio credentials:', err);
      // Not critical - user can still enter manually
    }
  };
  
  // Fetch training files from the backend API
  const fetchTrainingFilesFromAPI = async () => {
    if (!agentId) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/training-files`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.files && data.files.length > 0) {
          setTrainingFiles(data.files);
          console.log('[VoiceWizard] Loaded training files:', data.files.length);
        }
      }
    } catch (err) {
      console.error('Error fetching training files:', err);
      // Not critical - files can be re-uploaded
    }
  };
  
  const loadAgent = async () => {
    setLoading(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setAgent(data);
        
        // Determine if this is a booking agent
        const agentName = (data.name || '').toLowerCase();
        const isBooking = agentName.includes('booking') || 
                         agentName.includes('appointment') || 
                         agentName.includes('scheduler') ||
                         data.agent_config?.is_booking_agent;
        setIsBookingAgent(isBooking);
        
        // Phase A: Set voice mode from agent - check capability_profile first
        // capability_profile is the single source of truth
        const capabilityProfile = data.capability_profile;
        if (capabilityProfile === 'live_voice_s2s_agent') {
          setVoiceMode('live_s2s');
        } else if (capabilityProfile === 'voice_input_agent') {
          setVoiceMode('voice_input');
        } else if (capabilityProfile === 'voice_output_tts_agent') {
          setVoiceMode('voice_output');
        } else if (data.voice_mode) {
          // Fallback to voice_mode field if capability_profile not set
          setVoiceMode(data.voice_mode);
        }
        
        // Load existing configuration
        loadConfigFromAgent(data);
      } else {
        setError('Failed to load agent');
      }
    } catch (err) {
      console.error('Error loading agent:', err);
      setError('Failed to load agent configuration');
    } finally {
      setLoading(false);
    }
  };
  
  const loadConfigFromAgent = (data) => {
    // Voice config
    const voiceConfig = data.voice_config || {};
    if (voiceConfig.persona_name) setPersonaName(voiceConfig.persona_name);
    if (voiceConfig.persona_description) setPersonaDescription(voiceConfig.persona_description);
    if (voiceConfig.call_greeting) setGreetingMessage(voiceConfig.call_greeting);
    if (voiceConfig.interruptibility) setInterruptibility(voiceConfig.interruptibility);
    if (voiceConfig.max_call_duration_seconds) setMaxCallDuration(voiceConfig.max_call_duration_seconds / 60);
    if (voiceConfig.silence_timeout_seconds) setSilenceTimeout(voiceConfig.silence_timeout_seconds);
    if (voiceConfig.call_fallback_behavior) setFallbackBehavior(voiceConfig.call_fallback_behavior);
    if (voiceConfig.end_of_call_actions) setEndOfCallActions(voiceConfig.end_of_call_actions);
    if (voiceConfig.trigger_workflow_id) setSelectedWorkflowId(voiceConfig.trigger_workflow_id);
    if (voiceConfig.accent_locale) setAccentLocale(voiceConfig.accent_locale);
    if (voiceConfig.stt_provider) setSttProvider(voiceConfig.stt_provider);
    if (voiceConfig.tts_provider) setTtsProvider(voiceConfig.tts_provider);
    if (voiceConfig.tts_voice_id) setTtsVoiceId(voiceConfig.tts_voice_id);
    if (voiceConfig.special_notes_enabled) setSpecialNotesEnabled(voiceConfig.special_notes_enabled);
    if (voiceConfig.special_notes_destination) setSpecialNotesDestination(voiceConfig.special_notes_destination);
    if (voiceConfig.special_notes_prompt) setSpecialNotesPrompt(voiceConfig.special_notes_prompt);
    
    // SMS config
    const smsConfig = data.sms_config || {};
    if (smsConfig.enabled) setSmsEnabled(smsConfig.enabled);
    if (smsConfig.confirmation_enabled !== undefined) setSmsConfirmationEnabled(smsConfig.confirmation_enabled);
    if (smsConfig.confirmation_template) setSmsConfirmationTemplate(smsConfig.confirmation_template);
    if (smsConfig.reminder_enabled !== undefined) setSmsReminderEnabled(smsConfig.reminder_enabled);
    if (smsConfig.reminder_hours_before) setSmsReminderHours(smsConfig.reminder_hours_before);
    if (smsConfig.reminder_template) setSmsReminderTemplate(smsConfig.reminder_template);
    
    // Calendar config
    const calendarConfig = data.calendar_config || {};
    if (calendarConfig.enabled) setCalendarConnected(calendarConfig.enabled);
    if (calendarConfig.provider) setCalendarProvider(calendarConfig.provider);
    if (calendarConfig.calendar_id) setCalendarEmail(calendarConfig.calendar_id);
    if (calendarConfig.timezone) setTimezone(calendarConfig.timezone);
    if (calendarConfig.services) setServices(calendarConfig.services);
    if (calendarConfig.business_hours) {
      const bh = calendarConfig.business_hours;
      // Load per-day schedule if available
      if (bh.day_schedule) {
        setDaySchedule(prev => ({ ...prev, ...bh.day_schedule }));
      } else if (bh.working_days && bh.opening_time && bh.closing_time) {
        // Legacy: convert old format to new day_schedule format
        const legacySchedule = {
          monday: { enabled: bh.working_days.includes('monday'), opening: bh.opening_time, closing: bh.closing_time },
          tuesday: { enabled: bh.working_days.includes('tuesday'), opening: bh.opening_time, closing: bh.closing_time },
          wednesday: { enabled: bh.working_days.includes('wednesday'), opening: bh.opening_time, closing: bh.closing_time },
          thursday: { enabled: bh.working_days.includes('thursday'), opening: bh.opening_time, closing: bh.closing_time },
          friday: { enabled: bh.working_days.includes('friday'), opening: bh.opening_time, closing: bh.closing_time },
          saturday: { enabled: bh.working_days.includes('saturday'), opening: bh.opening_time, closing: bh.closing_time },
          sunday: { enabled: bh.working_days.includes('sunday'), opening: bh.opening_time, closing: bh.closing_time },
        };
        setDaySchedule(legacySchedule);
      }
      if (bh.buffer_minutes !== undefined) setBufferMinutes(bh.buffer_minutes);
      if (bh.min_notice_minutes !== undefined) setMinNoticeMinutes(bh.min_notice_minutes);
      if (bh.booking_horizon_days !== undefined) setBookingHorizonDays(bh.booking_horizon_days);
    }
    
    // Installation config (legacy support)
    const config = data.installation_config || {};
    const business = config.business || {};
    if (business.business_name) setBusinessName(business.business_name);
    if (business.business_description) setBusinessDescription(business.business_description);
    if (business.opening_message && !voiceConfig.call_greeting) setGreetingMessage(business.opening_message);
    if (business.required_booking_info) setRequiredBookingInfo(business.required_booking_info);
    // Load day_schedule from business config if not already loaded from calendar_config
    if (business.day_schedule && !calendarConfig.business_hours?.day_schedule) {
      setDaySchedule(prev => ({ ...prev, ...business.day_schedule }));
    }
    
    // Training files / knowledge base
    if (config.training_files) setTrainingFiles(config.training_files);
    if (config.custom_knowledge) setCustomKnowledge(config.custom_knowledge);
    
    // Fetch training files from the database (they're stored separately)
    fetchTrainingFilesFromAPI();
    
    // Booking provider config
    const bookingConfig = config.booking || {};
    if (bookingConfig.provider) setBookingProvider(bookingConfig.provider);
    
    // Load webhook booking config
    if (bookingConfig.webhook_url) setWebhookBookingUrl(bookingConfig.webhook_url);
    if (bookingConfig.webhook_method) setWebhookBookingMethod(bookingConfig.webhook_method);
    if (bookingConfig.webhook_headers && Object.keys(bookingConfig.webhook_headers).length > 0) {
      const headersArray = Object.entries(bookingConfig.webhook_headers).map(([key, value]) => ({ key, value }));
      setWebhookBookingHeaders(headersArray.length > 0 ? headersArray : [{ key: '', value: '' }]);
    }
    
    // Twilio config
    const twilioConfig = config.twilio || data.agent_config?.twilio_config || {};
    if (twilioConfig.twilio_account_sid) setTwilioAccountSid(twilioConfig.twilio_account_sid);
    if (twilioConfig.twilio_phone_number) setTwilioPhoneNumber(twilioConfig.twilio_phone_number);
    
    // Check if Twilio auth token exists (multiple signals)
    // 1. Explicit has_auth_token flag from backend
    // 2. twilio_auth_token value (legacy)
    // 3. sms_enabled is true (requires valid credentials)
    // 4. Agent has an active voice status
    const hasTwilioCredentials = twilioConfig.has_auth_token || 
                                  twilioConfig.twilio_auth_token ||
                                  twilioConfig.sms_enabled ||
                                  (twilioConfig.twilio_account_sid && data.status === 'active');
    
    if (hasTwilioCredentials) {
      setTwilioValidated(true);
      setHasExistingTwilioToken(true);
    }
    
    // Telnyx config
    const telnyxConfig = config.telnyx || {};
    if (telnyxConfig.phone_number) setTelnyxPhoneNumber(telnyxConfig.phone_number);
    
    // Check if Telnyx API key exists (multiple signals)
    const hasTelnyxCredentials = telnyxConfig.has_api_key ||
                                  telnyxConfig.api_key ||
                                  (telnyxConfig.phone_number && data.status === 'active');
    
    if (hasTelnyxCredentials) {
      setTelnyxValidated(true);
      setHasExistingTelnyxKey(true);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SAVE CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════
  const saveConfiguration = async (finalSave = false) => {
    setSaving(true);
    setError(null);
    
    try {
      // Build voice_config
      const voiceConfig = {
        enabled: true,
        persona_name: personaName,
        persona_description: personaDescription,
        call_greeting: greetingMessage,
        call_fallback_behavior: fallbackBehavior,
        interruptibility: interruptibility,
        max_call_duration_seconds: maxCallDuration * 60,
        silence_timeout_seconds: silenceTimeout,
        end_of_call_actions: endOfCallActions,
        trigger_workflow_id: endOfCallActions.includes('trigger_workflow') ? selectedWorkflowId : '',
        accent_locale: accentLocale,
        voice_conversation_mode: voiceConversationMode, // S2S or STT+TTS mode
        realtime_provider: realtimeProvider, // For S2S mode
        realtime_voice: realtimeVoice, // Voice for S2S (alloy, echo, etc.)
        stt_provider: sttProvider,
        tts_provider: ttsProvider,
        tts_voice_id: ttsVoiceId,
        special_notes_enabled: specialNotesEnabled,
        special_notes_destination: specialNotesEnabled ? specialNotesDestination : '',
        special_notes_prompt: specialNotesEnabled ? specialNotesPrompt : ''
      };
      
      // Log what we're saving for debugging
      console.log('[Wizard] Saving voice_config:', {
        persona_name: voiceConfig.persona_name,
        call_greeting: voiceConfig.call_greeting?.substring(0, 50) + '...',
        interruptibility: voiceConfig.interruptibility
      });
      
      // Build sms_config
      const smsConfigPayload = {
        enabled: smsEnabled,
        confirmation_enabled: smsConfirmationEnabled,
        confirmation_template: smsConfirmationTemplate,
        reminder_enabled: smsReminderEnabled,
        reminder_hours_before: smsReminderHours,
        reminder_template: smsReminderTemplate
      };
      
      // Build calendar_config (for booking agents)
      const calendarConfigPayload = isBookingAgent ? {
        enabled: calendarConnected,
        provider: calendarProvider,
        calendar_id: calendarEmail,
        timezone: timezone,
        business_hours: {
          working_days: workingDays,
          opening_time: openingTime,
          closing_time: closingTime,
          day_schedule: daySchedule,  // Per-day hours
          buffer_minutes: bufferMinutes,
          min_notice_minutes: minNoticeMinutes,
          booking_horizon_days: bookingHorizonDays
        },
        services: services.filter(s => s.service_name.trim())
      } : { enabled: false };
      
      // Build update payload
      const updatePayload = {
        voice_config: voiceConfig,
        sms_config: smsConfigPayload,
        calendar_config: calendarConfigPayload,
        wizard_completed: finalSave
      };
      
      // Add legacy installation_config for backward compatibility
      // Phase E: Unified telephony provider support
      const telephonyValidated = telephonyProvider === 'twilio' ? twilioValidated : telnyxValidated;
      
      updatePayload.installation_config = {
        business: {
          business_name: businessName,
          business_description: businessDescription,
          opening_message: greetingMessage,
          required_booking_info: requiredBookingInfo,
          timezone,
          working_days: workingDays,
          opening_time: openingTime,
          closing_time: closingTime,
          day_schedule: daySchedule,  // Per-day hours
          buffer_minutes: bufferMinutes,
          min_notice_minutes: minNoticeMinutes,
          booking_horizon_days: bookingHorizonDays
        },
        services: {
          services: services.filter(s => s.service_name.trim())
        },
        // Booking provider selection
        booking: {
          provider: bookingProvider,
          // Square and Calendly will require additional config when connected
          square_connected: false,
          calendly_connected: false,
          // WebhooksLogo configuration (when provider is 'webhook')
          ...(bookingProvider === 'webhook' && {
            webhook_url: webhookBookingUrl,
            webhook_method: webhookBookingMethod,
            webhook_headers: webhookBookingHeaders
              .filter(h => h.key && h.value)
              .reduce((acc, h) => ({ ...acc, [h.key]: h.value }), {})
          })
        },
        calendar: {
          calendar_provider: calendarProvider,
          calendar_connected: calendarConnected,
          calendar_email: calendarEmail
        },
        // Knowledge base text content
        custom_knowledge: customKnowledge,
        // Phase E: Unified telephony config
        telephony: {
          provider: telephonyProvider,
          validated: telephonyValidated,
          phone_number: telephonyProvider === 'twilio' ? twilioPhoneNumber : telnyxPhoneNumber
        },
        // Legacy Twilio config (for backward compatibility)
        twilio: telephonyProvider === 'twilio' ? {
          twilio_account_sid: twilioAccountSid,
          twilio_auth_token: twilioAuthToken,
          twilio_phone_number: twilioPhoneNumber,
          voice_enabled: voiceMode === 'live_s2s',
          sms_enabled: smsEnabled,
          validated: twilioValidated
        } : null,
        // Telnyx config
        telnyx: telephonyProvider === 'telnyx' ? {
          api_key: telnyxApiKey,
          phone_number: telnyxPhoneNumber,
          voice_enabled: voiceMode === 'live_s2s',
          sms_enabled: smsEnabled,
          validated: telnyxValidated
        } : null
      };
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updatePayload)
        },
        activeOrganization?.organization_id
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage;
        
        // Handle specific HTTP status codes with user-friendly messages
        if (response.status === 502 || response.status === 503 || response.status === 504) {
          errorMessage = 'Server is temporarily unavailable. Please try again in a few seconds.';
        } else if (response.status === 403) {
          // Tier validation error - provide clear guidance
          errorMessage = errorData.detail || 'Voice agents require a Pro or Partner Pro plan. Please upgrade to continue.';
        } else if (response.status === 401) {
          errorMessage = 'Your session has expired. Please refresh the page and log in again.';
        } else {
          errorMessage = errorData.detail?.message || errorData.detail || 'Failed to save configuration';
        }
        
        console.error('[Wizard] Save failed:', response.status, errorMessage);
        throw new Error(errorMessage);
      }
      
      console.log('[Wizard] Configuration saved successfully');
      
      // If final save and Twilio configured, connect Twilio integration
      if (finalSave && twilioValidated && twilioAccountSid && twilioAuthToken && twilioPhoneNumber) {
        await fetchWithOrg(
          `${BACKEND_URL}/api/integrations/twilio/connect`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              account_sid: twilioAccountSid,
              auth_token: twilioAuthToken,
              phone_number: twilioPhoneNumber,
              voice_enabled: voiceMode === 'live_s2s',
              sms_enabled: smsEnabled,
              agent_id: agentId
            })
          },
          activeOrganization?.organization_id
        );
      }
      
      return true;
    } catch (err) {
      console.error('Error saving:', err);
      setError(err.message);
      // Show toast for better user feedback
      toast.error(err.message || 'Failed to save configuration. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // TWILIO VALIDATION
  // ═══════════════════════════════════════════════════════════════════════════
  const validateTwilio = async () => {
    if (!twilioAccountSid || !twilioAuthToken) {
      setError('Please enter Account SID and Auth Token');
      return;
    }
    
    setValidatingTwilio(true);
    setError(null);
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/integrations/twilio/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            account_sid: twilioAccountSid,
            auth_token: twilioAuthToken
          })
        },
        activeOrganization?.organization_id
      );
      
      const data = await response.json();
      
      if (data.valid) {
        setTwilioValidated(true);
        fetchWebhookUrls();
      } else {
        setError(data.error || 'Invalid Twilio credentials');
      }
    } catch (err) {
      setError('Failed to validate Twilio credentials');
    } finally {
      setValidatingTwilio(false);
    }
  };
  
  // Phase E: Telnyx validation
  const validateTelnyx = async () => {
    if (!telnyxApiKey || !telnyxPhoneNumber) {
      setError('Please enter API Key and Phone Number');
      return;
    }
    
    setValidatingTelnyx(true);
    setError(null);
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/integrations/telnyx/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            api_key: telnyxApiKey,
            phone_number: telnyxPhoneNumber
          })
        },
        activeOrganization?.organization_id
      );
      
      const data = await response.json();
      
      if (data.valid) {
        setTelnyxValidated(true);
        fetchWebhookUrls();
      } else {
        setError(data.error || 'Invalid Telnyx credentials');
      }
    } catch (err) {
      setError('Failed to validate Telnyx credentials');
    } finally {
      setValidatingTelnyx(false);
    }
  };
  
  const fetchWebhookUrls = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/twilio-webhooks`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setWebhookUrls(data);
      }
    } catch (err) {
      console.error('Failed to fetch webhook URLs:', err);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GOOGLE CALENDAR CONNECTION (Phase 4 - P1 Fix)
  // ═══════════════════════════════════════════════════════════════════════════
  const connectGoogleCalendar = async () => {
    setConnectingCalendar(true);
    setError(null);
    
    try {
      // Get OAuth URL for Google Calendar
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/integrations/google/calendar/auth-url?agent_id=${agentId}`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        const errorData = await response.json().catch(() => ({}));
        // Check if error is about OAuth not configured
        if (errorData.detail?.includes('not configured') || errorData.detail?.includes('Settings')) {
          setCalendarOAuthConfigured(false);
          setError('Admin setup required: Google Workspace OAuth credentials must be configured in Settings → Integrations before users can connect calendars.');
        } else {
          setError(errorData.detail || 'Failed to start Google Calendar connection');
        }
      }
    } catch (err) {
      console.error('Error connecting calendar:', err);
      setError('Failed to connect to Google Calendar');
    } finally {
      setConnectingCalendar(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MICROSOFT CALENDAR CONNECTION (Phase 5 - P0)
  // ═══════════════════════════════════════════════════════════════════════════
  const connectMicrosoftCalendar = async () => {
    setConnectingCalendar(true);
    setError(null);
    
    try {
      // Get OAuth URL for Microsoft Calendar
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/integrations/microsoft/calendar/auth-url?agent_id=${agentId}`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Microsoft OAuth
        window.location.href = data.auth_url;
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to start Microsoft Calendar connection');
      }
    } catch (err) {
      console.error('Error connecting Microsoft calendar:', err);
      setError('Failed to connect to Microsoft Calendar');
    } finally {
      setConnectingCalendar(false);
    }
  };
  
  // Check if returning from OAuth callback (Google or Microsoft)
  useEffect(() => {
    const calendarConnectedParam = searchParams.get('calendar_connected');
    const calendarEmailParam = searchParams.get('calendar_email');
    const calendarProviderParam = searchParams.get('calendar_provider');
    
    if (calendarConnectedParam === 'true' && calendarEmailParam) {
      setCalendarConnected(true);
      setCalendarEmail(calendarEmailParam);
      // Set the provider if returned from OAuth
      if (calendarProviderParam === 'microsoft') {
        setCalendarProvider('microsoft');
      } else if (calendarProviderParam === 'google') {
        setCalendarProvider('google');
      }
      
      // Jump to the calendar step
      const calendarStepIndex = steps.findIndex(s => s.id === 'calendar');
      if (calendarStepIndex !== -1) {
        setCurrentStep(calendarStepIndex);
      }
      
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams, steps]);

  // ═══════════════════════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════
  const nextStep = async () => {
    // Prevent double-clicks while saving or advancing
    if (saving || advancing) {
      console.log('[Wizard] Already saving/advancing, skipping nextStep');
      return;
    }
    
    setAdvancing(true);
    
    try {
      // Save current step
      console.log('[Wizard] Saving configuration before advancing from step', currentStep);
      const saved = await saveConfiguration(false);
      console.log('[Wizard] Save result:', saved, 'Current step:', currentStep, 'Total steps:', steps.length);
      
      if (saved && currentStep < steps.length - 1) {
        console.log('[Wizard] Advancing to step', currentStep + 1);
        // Use functional update to ensure we get the latest step value
        setCurrentStep(prevStep => {
          const newStep = prevStep + 1;
          console.log('[Wizard] Setting step from', prevStep, 'to', newStep);
          return newStep;
        });
      } else if (!saved) {
        console.error('[Wizard] Save failed, not advancing');
      }
    } finally {
      setAdvancing(false);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const finishWizard = async () => {
    const saved = await saveConfiguration(true);
    if (saved) {
      if (isModal && onModalClose) {
        onModalClose(agentId);
      } else {
        navigate(`/partner-pro/ai-workforce/agents/${agentId}`);
      }
    }
  };
  
  // Phase G: Exit to Advanced Editor - always go to the agent's edit page with advanced mode
  const exitToEditor = () => {
    // Save without marking complete, then navigate to advanced editor
    saveConfiguration(false).then(() => {
      if (isModal && onModalClose) {
        onModalClose(agentId);
      } else {
        // Use ?advanced=true to skip wizard redirect in AgentEditor
        navigate(`/partner-pro/ai-workforce/agents/${agentId}/edit?advanced=true`);
      }
    });
  };
  
  // Phase G: Cancel wizard with confirmation
  const handleCancel = () => {
    setShowCancelConfirm(true);
  };
  
  const confirmCancel = () => {
    setShowCancelConfirm(false);
    if (isModal && onModalClose) {
      onModalClose(null);
    } else {
      // Navigate back to agent detail page (discard changes)
      navigate(`/partner-pro/ai-workforce/agents/${agentId}`);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER STEP CONTENT
  // ═══════════════════════════════════════════════════════════════════════════
  const renderStepContent = () => {
    const step = steps[currentStep];
    if (!step) return null;
    
    switch (step.id) {
      case 'voice_mode':
        return renderVoiceModeStep();
      case 'persona':
        return renderPersonaStep();
      case 'knowledge_base':
        return renderKnowledgeBaseStep();
      case 'call_behavior':
        return renderCallBehaviorStep();
      case 'booking_provider':
        return renderBookingProviderStep();
      case 'data_collection':
        return renderDataCollectionStep();
      case 'services':
        return renderServicesStep();
      case 'calendar':
        return renderCalendarStep();
      case 'sms':
        return renderSmsStep();
      case 'voice_settings':
        return renderVoiceSettingsStep();
      case 'twilio':
      case 'telephony':
        return renderTelephonyStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 1: Voice Mode (Phase A: Locked support for template and capability profile)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderVoiceModeStep = () => {
    // Phase A: If voice mode is locked (either from template or capability_profile), show locked state
    if (isVoiceModeLocked) {
      const currentMode = VOICE_MODES.find(m => m.id === voiceMode) || VOICE_MODES[0];
      const CurrentModeIcon = currentMode.icon;
      
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Voice Mode</h3>
            <p className="text-sm text-[#B0B8C4]">Your worker uses real-time speech-to-speech voice</p>
          </div>
          
          {/* Locked mode display */}
          <div className="p-6 rounded-xl border-2 border-[#00E5C0] bg-[#00E5C0]/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#00E5C0]/20 flex items-center justify-center">
                <CurrentModeIcon size={20} className="text-[#00E5C0]" />
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <CheckCircle size={16} className="text-[#00E5C0]" />
                <span className="text-xs text-[#00E5C0] uppercase tracking-wide">Active</span>
              </div>
            </div>
            <h4 className="font-semibold text-white mb-1">{currentMode.label}</h4>
            <p className="text-sm text-[#B0B8C4]">{currentMode.description}</p>
          </div>
        </div>
      );
    }
    
    // Standard mode selection for manually created agents
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">Select Voice Mode</h3>
          <p className="text-sm text-[#B0B8C4]">Choose how your agent will interact with voice</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {VOICE_MODES.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setVoiceMode(mode.id)}
              className={`p-6 rounded-xl border-2 text-left transition-all ${
                voiceMode === mode.id
                  ? `border-${mode.color}-500 bg-${mode.color}-500/10`
                  : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg bg-${mode.color}-500/20 flex items-center justify-center`}>
                  <mode.icon size={20} className={`text-${mode.color}-400`} />
                </div>
                {voiceMode === mode.id && (
                  <Check size={20} className="text-[#00E5C0] ml-auto" />
                )}
              </div>
              <h4 className="font-semibold text-white mb-1">{mode.label}</h4>
              <p className="text-sm text-[#B0B8C4]">{mode.description}</p>
              {mode.requiresTwilio && (
                <p className="text-xs text-amber-400 mt-2">Requires Telephony</p>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2: Persona & Greeting
  // ═══════════════════════════════════════════════════════════════════════════
  const renderPersonaStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Persona & Greeting</h3>
        <p className="text-sm text-[#B0B8C4]">Define your agent&apos;s personality and how it greets callers</p>
      </div>
      
      {/* Persona Name */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Persona Name *
        </label>
        <input
          type="text"
          value={personaName}
          onChange={(e) => setPersonaName(e.target.value)}
          placeholder="e.g., Sarah, Alex, Your Business Assistant"
          className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
        />
      </div>
      
      {/* Persona Description */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Persona Description
        </label>
        <textarea
          value={personaDescription}
          onChange={(e) => setPersonaDescription(e.target.value)}
          placeholder="Describe your agent's personality, tone, and style..."
          rows={3}
          className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
        />
      </div>
      
      {/* Greeting ChatText - REQUIRED */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Opening/Greeting ChatText *
        </label>
        <textarea
          value={greetingMessage}
          onChange={(e) => setGreetingMessage(e.target.value)}
          placeholder="e.g., Hello, thanks for calling [Business Name]. This is [Persona Name], how can I help you today?"
          rows={3}
          className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
        />
        <p className="text-xs text-[#7D8896] mt-1">
          This is the first thing callers will hear when the agent answers
        </p>
      </div>
      
      {isBookingAgent && (
        <>
          {/* Business Name */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Business Name
            </label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Your business name"
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
            />
          </div>
          
          {/* Business Description */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Business Description
            </label>
            <textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              placeholder="Brief description of your business for context..."
              rows={2}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
            />
          </div>
        </>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: Knowledge Base / Training Data
  // ═══════════════════════════════════════════════════════════════════════════
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingTrainingData(true);
    
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      formData.append('agent_id', agentId);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/training-files`, 
        {
          method: 'POST',
          body: formData
        },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setTrainingFiles(prev => [...prev, ...data.files]);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to upload files');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload files');
    } finally {
      setUploadingTrainingData(false);
      event.target.value = ''; // Reset input
    }
  };
  
  const handleRemoveFile = async (fileId) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/training-files/${fileId}`, 
        {
          method: 'DELETE'
        },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        setTrainingFiles(prev => prev.filter(f => f.id !== fileId));
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };
  
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['txt', 'md'].includes(ext)) return '📃';
    return '📁';
  };
  
  const renderKnowledgeBaseStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Knowledge Base</h3>
        <p className="text-sm text-[#B0B8C4]">
          UploadSimple documents to give your voice agent additional context about your business. 
          This helps the agent answer questions more accurately.
        </p>
      </div>
      
      {/* UploadSimple Area */}
      <div className="border-2 border-dashed border-[rgba(255,255,255,0.2)] rounded-xl p-8 text-center hover:border-[#00E5C0]/50 transition-colors">
        <input
          type="file"
          id="training-file-upload"
          className="hidden"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md"
          onChange={handleFileUpload}
          disabled={uploadingTrainingData}
        />
        <label 
          htmlFor="training-file-upload"
          className="cursor-pointer flex flex-col items-center gap-3"
        >
          {uploadingTrainingData ? (
            <CircleNotch size={40} className="text-[#00E5C0] animate-spin" />
          ) : (
            <UploadSimple size={40} className="text-[#7D8896]" />
          )}
          <div>
            <p className="text-white font-medium">
              {uploadingTrainingData ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-[#7D8896] mt-1">
              PDF, DOC, DOCX, XLS, XLSX, CSV, TXT, MD (Max 10MB per file)
            </p>
          </div>
        </label>
      </div>
      
      {/* Uploaded Files List */}
      {trainingFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-[#F0F4F8]">Uploaded Documents</h4>
          <div className="space-y-2">
            {trainingFiles.map((file) => (
              <div 
                key={file.id}
                className="flex items-center gap-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.1)]"
              >
                <span className="text-xl">{getFileIcon(file.filename)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{file.filename}</p>
                  <p className="text-xs text-[#7D8896]">
                    {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Processing...'}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveFile(file.id)}
                  className="p-1.5 text-[#B0B8C4] hover:text-[#FF6B6B] hover:bg-[rgba(255,107,107,0.08)] rounded transition-colors"
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Custom Text Knowledge */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-[#F0F4F8]">Custom Knowledge (Text)</h4>
            <p className="text-xs text-[#7D8896] mt-0.5">
              Add FAQs, policies, product info, or any text-based knowledge
            </p>
          </div>
          {customKnowledge && (
            <span className="text-xs text-[#7D8896]">
              {customKnowledge.length.toLocaleString()} characters
            </span>
          )}
        </div>
        <textarea
          value={customKnowledge}
          onChange={(e) => setCustomKnowledge(e.target.value)}
          placeholder={`Example:

Q: What are your business hours?
A: We're open Monday to Friday 9am-5pm, Saturday 10am-2pm.

Q: What's your cancellation policy?
A: Please cancel at least 24 hours in advance to avoid a fee.

Q: Do you offer refunds?
A: Yes, we offer full refunds within 7 days of purchase.

Products & Services:
- Haircut: $30 (30 mins)
- Color: $80 (90 mins)
- Styling: $50 (45 mins)`}
          className="w-full h-48 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-white placeholder-gray-600 text-sm resize-y focus:border-[#00E5C0]/50 focus:outline-none transition-colors"
        />
      </div>
      
      {/* Tips */}
      <div className="p-4 bg-[rgba(0,229,192,0.1)] rounded-lg border border-[rgba(99,102,241,0.2)]">
        <h4 className="text-sm font-medium text-[#00E5C0] mb-2">💡 Tips for better responses</h4>
        <ul className="text-xs text-[#B0B8C4] space-y-1">
          <li>• UploadSimple your service menu, price list, or FAQ documents</li>
          <li>• Include staff bios or specialist information</li>
          <li>• Add product catalogs or inventory lists</li>
          <li>• Include policies (cancellation, refund, etc.)</li>
        </ul>
      </div>
      
      {/* Optional - Skip notice */}
      <p className="text-xs text-[#7D8896] text-center">
        This step is optional. You can skip it and add documents later from the agent settings.
      </p>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 3: Call Behaviour
  // ═══════════════════════════════════════════════════════════════════════════
  const renderCallBehaviorStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Call Behaviour</h3>
        <p className="text-sm text-[#B0B8C4]">Configure how your agent handles calls</p>
      </div>
      
      {/* Interruptibility */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Interruptibility
        </label>
        <div className="space-y-2">
          {INTERRUPTIBILITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setInterruptibility(option.value)}
              className={`w-full p-4 rounded-lg border text-left transition-all ${
                interruptibility === option.value
                  ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
                  : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-white">{option.label}</h4>
                  <p className="text-sm text-[#B0B8C4]">{option.description}</p>
                </div>
                {interruptibility === option.value && (
                  <Check size={20} className="text-[#00E5C0]" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Max Call Duration */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Maximum Call Duration (minutes)
        </label>
        <input
          type="number"
          value={maxCallDuration}
          onChange={(e) => setMaxCallDuration(parseInt(e.target.value) || 15)}
          min={1}
          max={60}
          className="w-32 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
        />
      </div>
      
      {/* Silence Timeout */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Silence Timeout (seconds)
        </label>
        <input
          type="number"
          value={silenceTimeout}
          onChange={(e) => setSilenceTimeout(parseInt(e.target.value) || 5)}
          min={1}
          max={30}
          className="w-32 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
        />
        <p className="text-xs text-[#7D8896] mt-1">How long to wait before prompting the caller</p>
      </div>
      
      {/* Fallback Behaviour */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Fallback Behaviour
        </label>
        <textarea
          value={fallbackBehavior}
          onChange={(e) => setFallbackBehavior(e.target.value)}
          placeholder="What should the agent say/do if it can't understand or help?"
          rows={2}
          className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
        />
      </div>
      
      {/* End of Call Actions */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          End of Call Actions
        </label>
        <div className="grid grid-cols-2 gap-3">
          {END_OF_CALL_ACTIONS.map((action) => (
            <button
              key={action.id}
              onClick={() => {
                setEndOfCallActions(prev =>
                  prev.includes(action.id)
                    ? prev.filter(a => a !== action.id)
                    : [...prev, action.id]
                );
              }}
              className={`p-3 rounded-lg border text-left transition-all ${
                endOfCallActions.includes(action.id)
                  ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
                  : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
              }`}
            >
              <h4 className="font-medium text-white text-sm">{action.label}</h4>
              <p className="text-xs text-[#B0B8C4]">{action.description}</p>
            </button>
          ))}
        </div>
        
        {/* Workflow Selector - shown when Trigger Workflow is selected */}
        {endOfCallActions.includes('trigger_workflow') && (
          <div className="mt-4 p-4 rounded-lg bg-[rgba(0,229,192,0.1)] border border-[#00E5C0]/30">
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Select Workflow to Trigger
            </label>
            {availableWorkflows.length > 0 ? (
              <select
                value={selectedWorkflowId}
                onChange={(e) => setSelectedWorkflowId(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
              >
                <option value="">-- Select a workflow --</option>
                {availableWorkflows.map((wf) => (
                  <option key={wf.workflow_id || wf.id} value={wf.workflow_id || wf.id}>
                    {wf.name || wf.workflow_name || 'Unnamed Workflow'}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-sm text-[#B0B8C4]">
                <p>No workflows found. Create a workflow first in <strong>Partner Pro → Workspace → Workflows</strong></p>
              </div>
            )}
            <p className="text-xs text-[#7D8896] mt-2">
              The selected workflow will be triggered automatically when the call ends.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: Booking Provider Selection
  // ═══════════════════════════════════════════════════════════════════════════
  const renderBookingProviderStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Select Booking Provider</h3>
        <p className="text-sm text-[#B0B8C4]">Choose your preferred booking system for appointment management</p>
      </div>
      
      {/* Recommended Providers */}
      <div>
        <h4 className="text-sm font-medium text-[#00E5C0] mb-3 flex items-center gap-2">
          <Star size={14} />
          Recommended
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {BOOKING_PROVIDERS.filter(p => p.category === 'recommended').map((provider) => (
            <button
              key={provider.id}
              onClick={() => !provider.comingSoon && setBookingProvider(provider.id)}
              disabled={provider.comingSoon}
              className={`relative p-5 rounded-xl border-2 text-left transition-all ${
                bookingProvider === provider.id
                  ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
                  : provider.comingSoon 
                    ? 'border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)] opacity-60 cursor-not-allowed'
                    : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 ${provider.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-white">{provider.name}</h4>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] rounded text-[9px] font-semibold text-white">
                      <Star className="w-2.5 h-2.5" />
                      Recommended
                    </span>
                    {provider.comingSoon && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#00D4B0]/30 rounded text-[9px] font-medium text-[#00D4B0]">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#B0B8C4]">{provider.description}</p>
                </div>
              </div>
              {bookingProvider === provider.id && (
                <Check size={18} className="text-[#00E5C0] absolute top-3 right-3" />
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Advanced Providers */}
      <div>
        <h4 className="text-sm font-medium text-[#B0B8C4] mb-3">Advanced</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {BOOKING_PROVIDERS.filter(p => p.category === 'advanced').map((provider) => (
            <button
              key={provider.id}
              onClick={() => !provider.comingSoon && setBookingProvider(provider.id)}
              disabled={provider.comingSoon}
              className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                bookingProvider === provider.id
                  ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
                  : provider.comingSoon 
                    ? 'border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)] opacity-60 cursor-not-allowed'
                    : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 ${provider.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  {provider.id === 'google' ? (
                    <svg viewBox="0 0 24 24" className="w-4 h-4">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  ) : provider.id === 'microsoft' ? (
                    <svg viewBox="0 0 21 21" className="w-4 h-4">
                      <rect fill="#f25022" x="1" y="1" width="9" height="9"/>
                      <rect fill="#00a4ef" x="1" y="11" width="9" height="9"/>
                      <rect fill="#7fba00" x="11" y="1" width="9" height="9"/>
                      <rect fill="#ffb900" x="11" y="11" width="9" height="9"/>
                    </svg>
                  ) : (
                    <PaperPlaneRight className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-white text-sm">{provider.name}</h4>
                    {provider.comingSoon && (
                      <span className="text-[9px] text-[#00D4B0]">Soon</span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#7D8896] truncate">{provider.description}</p>
                </div>
              </div>
              {bookingProvider === provider.id && (
                <Check size={14} className="text-[#00E5C0] absolute top-2 right-2" />
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Info Note */}
      <div className="p-4 bg-[rgba(0,229,192,0.1)] rounded-lg border border-[rgba(99,102,241,0.2)]">
        <p className="text-xs text-[#B0B8C4]">
          <span className="text-[#00E5C0] font-medium">Note:</span> You can connect your booking provider in GearSix → Integrations. 
          The actual connection is not required to continue with this wizard.
        </p>
      </div>
      
      {/* WebhooksLogo Configuration - Only shown when webhook is selected */}
      {bookingProvider === 'webhook' && (
        <div className="space-y-4 p-5 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
          <div className="flex items-center gap-2 mb-2">
            <WebhooksLogo size={18} className="text-[#00D4B0]" />
            <h4 className="font-semibold text-white">WebhooksLogo Configuration</h4>
          </div>
          <p className="text-xs text-[#B0B8C4] mb-4">
            Configure your custom booking webhook endpoint. When a booking is made, we will send a POST request with the booking details.
          </p>
          
          {/* WebhooksLogo URL */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              WebhooksLogo URL <span className="text-[#FF6B6B]">*</span>
            </label>
            <input
              type="url"
              value={webhookBookingUrl}
              onChange={(e) => setWebhookBookingUrl(e.target.value)}
              placeholder="https://your-api.com/api/bookings"
              className="w-full px-4 py-3 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 focus:outline-none focus:border-[#00E5C0] transition-colors"
              data-testid="webhook-booking-url"
            />
            <p className="text-xs text-[#7D8896] mt-1">
              The endpoint where booking requests will be sent
            </p>
          </div>
          
          {/* HTTP Method */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              HTTP Method
            </label>
            <select
              value={webhookBookingMethod}
              onChange={(e) => setWebhookBookingMethod(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white focus:outline-none focus:border-[#00E5C0] transition-colors"
              data-testid="webhook-booking-method"
            >
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
            </select>
          </div>
          
          {/* Custom Headers */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Custom Headers (Optional)
            </label>
            <p className="text-xs text-[#7D8896] mb-2">
              Add authentication or custom headers to your webhook request
            </p>
            <div className="space-y-2">
              {webhookBookingHeaders.map((header, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={header.key}
                    onChange={(e) => {
                      const newHeaders = [...webhookBookingHeaders];
                      newHeaders[index].key = e.target.value;
                      setWebhookBookingHeaders(newHeaders);
                    }}
                    placeholder="Header name (e.g., Authorization)"
                    className="flex-1 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#00E5C0]"
                  />
                  <input
                    type="text"
                    value={header.value}
                    onChange={(e) => {
                      const newHeaders = [...webhookBookingHeaders];
                      newHeaders[index].value = e.target.value;
                      setWebhookBookingHeaders(newHeaders);
                    }}
                    placeholder="Header value"
                    className="flex-1 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#00E5C0]"
                  />
                  {webhookBookingHeaders.length > 1 && (
                    <button
                      onClick={() => {
                        const newHeaders = webhookBookingHeaders.filter((_, i) => i !== index);
                        setWebhookBookingHeaders(newHeaders);
                      }}
                      className="p-2 rounded-lg hover:bg-[rgba(255,107,107,0.12)] text-[#FF6B6B] transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setWebhookBookingHeaders([...webhookBookingHeaders, { key: '', value: '' }])}
                className="flex items-center gap-1 text-sm text-[#00E5C0] hover:text-[#818CF8] transition-colors"
              >
                <Plus size={14} />
                Add Header
              </button>
            </div>
          </div>
          
          {/* Payload Preview */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Payload Preview
            </label>
            <pre className="p-3 rounded-lg bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.1)] text-xs text-[#F0F4F8] overflow-x-auto">
{`{
  "customer_name": "John Smith",
  "customer_phone": "+1234567890",
  "customer_email": "john@example.com",
  "service_name": "Consultation",
  "booking_date": "2025-01-25",
  "booking_time": "14:00",
  "duration_minutes": 30,
  "notes": "Customer notes...",
  "agent_id": "${agentId || 'agent_xxx'}",
  "organization_id": "${activeOrganization?.organization_id || 'org_xxx'}"
}`}
            </pre>
          </div>
          
          {/* Test WebhooksLogo Button */}
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                if (!webhookBookingUrl) {
                  toast.error('Please enter a webhook URL');
                  return;
                }
                setTestingWebhookBooking(true);
                setWebhookBookingTestResult(null);
                try {
                  // Build headers object
                  const headersObj = {};
                  webhookBookingHeaders.forEach(h => {
                    if (h.key && h.value) {
                      headersObj[h.key] = h.value;
                    }
                  });
                  
                  const testPayload = {
                    test: true,
                    customer_name: "Test Customer",
                    customer_phone: "+1234567890",
                    service_name: "Test Booking",
                    booking_date: new Date().toISOString().split('T')[0],
                    booking_time: "10:00",
                    agent_id: agentId || 'test_agent'
                  };
                  
                  const response = await fetch(webhookBookingUrl, {
                    method: webhookBookingMethod,
                    headers: {
                      'Content-Type': 'application/json',
                      ...headersObj
                    },
                    body: JSON.stringify(testPayload)
                  });
                  
                  if (response.ok) {
                    setWebhookBookingTestResult({ success: true, status: response.status });
                    toast.success('Webhook test successful!');
                  } else {
                    setWebhookBookingTestResult({ success: false, status: response.status, error: response.statusText });
                    toast.error(`Webhook test failed: ${response.status} ${response.statusText}`);
                  }
                } catch (error) {
                  setWebhookBookingTestResult({ success: false, error: error.message });
                  toast.error(`Webhook test failed: ${error.message}`);
                } finally {
                  setTestingWebhookBooking(false);
                }
              }}
              disabled={!webhookBookingUrl || testingWebhookBooking}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E5C0] hover:opacity-90 disabled:bg-gray-600 disabled:cursor-not-allowed text-[#0B1120] text-sm font-medium transition-colors"
              data-testid="test-webhook-btn"
            >
              {testingWebhookBooking ? (
                <>
                  <CircleNotch size={14} className="animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Lightning size={14} />
                  Test WebhooksLogo
                </>
              )}
            </button>
            
            {webhookBookingTestResult && (
              <span className={`text-sm ${webhookBookingTestResult.success ? 'text-[#00E5C0]' : 'text-[#FF6B6B]'}`}>
                {webhookBookingTestResult.success 
                  ? `✓ Success (${webhookBookingTestResult.status})` 
                  : `✗ Failed: ${webhookBookingTestResult.error || webhookBookingTestResult.status}`}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 4: Data Collection (Booking agents)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderDataCollectionStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Data Collection</h3>
        <p className="text-sm text-[#B0B8C4]">Configure what information to collect from callers</p>
      </div>
      
      {/* Required Booking Info */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-3">
          Required Information
        </label>
        <div className="space-y-2">
          {BOOKING_INFO_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => {
                if (option.required) return; // Can't toggle required fields
                setRequiredBookingInfo(prev =>
                  prev.includes(option.id)
                    ? prev.filter(i => i !== option.id)
                    : [...prev, option.id]
                );
              }}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                requiredBookingInfo.includes(option.id)
                  ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
                  : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
              } ${option.required ? 'cursor-default' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-white">{option.label}</h4>
                  <p className="text-sm text-[#B0B8C4]">{option.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {option.required && (
                    <span className="text-xs text-amber-400">Required</span>
                  )}
                  {requiredBookingInfo.includes(option.id) && (
                    <Check size={20} className="text-[#00E5C0]" />
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Special Notes Configuration (Phase 4 Fix) */}
      <div className="p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-white">Special Notes</h4>
            <p className="text-sm text-[#B0B8C4]">Collect and store special requests or notes</p>
          </div>
          <button
            onClick={() => setSpecialNotesEnabled(!specialNotesEnabled)}
            className={`w-12 h-6 rounded-full transition-colors ${
              specialNotesEnabled ? 'bg-[#00E5C0]' : 'bg-gray-600'
            }`}
          >
            <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
              specialNotesEnabled ? 'translate-x-6' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
        
        {specialNotesEnabled && (
          <div className="space-y-4 pt-4 border-t border-[rgba(255,255,255,0.1)]">
            {/* Notes Prompt */}
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Notes Prompt *
              </label>
              <textarea
                value={specialNotesPrompt}
                onChange={(e) => setSpecialNotesPrompt(e.target.value)}
                placeholder="How should the agent ask for special notes? e.g., 'Is there anything special you'd like us to know about your appointment?'"
                rows={2}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
              />
            </div>
            
            {/* Notes Destination */}
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Where to Save Notes *
              </label>
              <div className="space-y-2">
                {SPECIAL_NOTES_DESTINATIONS.map((dest) => (
                  <button
                    key={dest.value}
                    onClick={() => setSpecialNotesDestination(dest.value)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      specialNotesDestination === dest.value
                        ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
                        : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-white">{dest.label}</h4>
                        <p className="text-sm text-[#B0B8C4]">{dest.description}</p>
                      </div>
                      {specialNotesDestination === dest.value && (
                        <Check size={20} className="text-[#00E5C0]" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 5: Services Catalog (Booking agents)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderServicesStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Services Catalog</h3>
        <p className="text-sm text-[#B0B8C4]">Define the services available for booking</p>
      </div>
      
      {/* Business Hours */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
            Timezone
          </label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full md:w-1/2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Per-Day Working Hours */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-3">
          Working Hours by Day
        </label>
        <p className="text-[#7D8896] text-sm mb-4">
          Set different working hours for each day. Toggle a day off to mark it as unavailable.
        </p>
        <div className="space-y-2">
          {WEEKDAYS.map((day) => (
            <div 
              key={day.value}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                daySchedule[day.value]?.enabled
                  ? 'border-[rgba(99,102,241,0.3)] bg-[rgba(0,229,192,0.05)]'
                  : 'border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.02)] opacity-60'
              }`}
            >
              {/* Day toggle button */}
              <button
                onClick={() => {
                  setDaySchedule(prev => ({
                    ...prev,
                    [day.value]: { ...prev[day.value], enabled: !prev[day.value]?.enabled }
                  }));
                }}
                className={`w-16 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  daySchedule[day.value]?.enabled
                    ? 'bg-[#00E5C0] text-white'
                    : 'bg-[rgba(255,255,255,0.1)] text-[#B0B8C4]'
                }`}
              >
                {day.label}
              </button>
              
              {/* Time inputs */}
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="time"
                  value={daySchedule[day.value]?.opening || '09:00'}
                  onChange={(e) => {
                    setDaySchedule(prev => ({
                      ...prev,
                      [day.value]: { ...prev[day.value], opening: e.target.value }
                    }));
                  }}
                  disabled={!daySchedule[day.value]?.enabled}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-sm disabled:opacity-40"
                />
                <span className="text-[#7D8896] text-sm">to</span>
                <input
                  type="time"
                  value={daySchedule[day.value]?.closing || '17:00'}
                  onChange={(e) => {
                    setDaySchedule(prev => ({
                      ...prev,
                      [day.value]: { ...prev[day.value], closing: e.target.value }
                    }));
                  }}
                  disabled={!daySchedule[day.value]?.enabled}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-sm disabled:opacity-40"
                />
              </div>
              
              {/* Closed indicator */}
              {!daySchedule[day.value]?.enabled && (
                <span className="text-[#7D8896] text-sm italic">Closed</span>
              )}
            </div>
          ))}
        </div>
        
        {/* Quick actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => {
              const firstEnabled = Object.entries(daySchedule).find(([_, v]) => v.enabled);
              if (firstEnabled) {
                const [_, schedule] = firstEnabled;
                setDaySchedule(prev => {
                  const updated = { ...prev };
                  Object.keys(updated).forEach(day => {
                    if (updated[day].enabled) {
                      updated[day] = { ...updated[day], opening: schedule.opening, closing: schedule.closing };
                    }
                  });
                  return updated;
                });
              }
            }}
            className="text-sm text-[#00E5C0] hover:text-[#818CF8] transition-colors"
          >
            Apply same hours to all enabled days
          </button>
        </div>
      </div>
      
      {/* Services List */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Services
        </label>
        <div className="space-y-3">
          {services.map((service, idx) => (
            <div key={idx} className="p-4 bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.1)]">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={service.service_name}
                  onChange={(e) => {
                    const updated = [...services];
                    updated[idx].service_name = e.target.value;
                    setServices(updated);
                  }}
                  placeholder="Service name"
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white placeholder-gray-500"
                />
                <input
                  type="number"
                  value={service.duration_minutes}
                  onChange={(e) => {
                    const updated = [...services];
                    updated[idx].duration_minutes = parseInt(e.target.value) || 30;
                    setServices(updated);
                  }}
                  placeholder="Duration (min)"
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white placeholder-gray-500"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={service.price}
                    onChange={(e) => {
                      const updated = [...services];
                      updated[idx].price = e.target.value;
                      setServices(updated);
                    }}
                    placeholder="Price (optional)"
                    className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white placeholder-gray-500"
                  />
                  {services.length > 1 && (
                    <button
                      onClick={() => setServices(services.filter((_, i) => i !== idx))}
                      className="p-2 text-[#FF6B6B] hover:bg-[rgba(255,107,107,0.08)] rounded-lg"
                    >
                      <Trash size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={() => setServices([...services, { service_name: '', duration_minutes: 30, price: '', description: '' }])}
          className="mt-3 flex items-center gap-2 text-[#00E5C0] hover:text-[#00D4B0]"
        >
          <Plus size={18} />
          Add Service
        </button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 6: Calendar Connection
  // ═══════════════════════════════════════════════════════════════════════════
  const renderCalendarStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Calendar Connection</h3>
        <p className="text-sm text-[#B0B8C4]">Connect your calendar to enable booking</p>
      </div>
      
      {/* Calendar Provider Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Google Calendar */}
        <button
          onClick={() => setCalendarProvider('google')}
          className={`p-6 rounded-xl border-2 text-left transition-all ${
            calendarProvider === 'google'
              ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
              : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-white">Google Calendar</h4>
              <p className="text-sm text-[#B0B8C4]">Connect your Google account</p>
            </div>
          </div>
          {calendarProvider === 'google' && (
            <Check size={20} className="text-[#00E5C0] absolute top-4 right-4" />
          )}
        </button>
        
        {/* Microsoft Calendar */}
        <button
          onClick={() => setCalendarProvider('microsoft')}
          className={`p-6 rounded-xl border-2 text-left transition-all ${
            calendarProvider === 'microsoft'
              ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
              : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#0078D4] rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <path fill="#fff" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2V9h2v8zm0-10h-2V5h2v2zm4 10h-2V9h2v8zm0-10h-2V5h2v2z"/>
                <rect fill="#fff" x="3" y="3" width="8" height="8"/>
                <rect fill="#fff" x="13" y="3" width="8" height="8"/>
                <rect fill="#fff" x="3" y="13" width="8" height="8"/>
                <rect fill="#fff" x="13" y="13" width="8" height="8"/>
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-white">Microsoft Calendar</h4>
              <p className="text-sm text-[#B0B8C4]">Connect your Microsoft 365 account</p>
            </div>
          </div>
          {calendarProvider === 'microsoft' && (
            <Check size={20} className="text-[#00E5C0] absolute top-4 right-4" />
          )}
        </button>
        
        {/* Calendly */}
        <button
          onClick={() => setCalendarProvider('calendly')}
          className={`p-6 rounded-xl border-2 text-left transition-all ${
            calendarProvider === 'calendly'
              ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
              : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#006BFF] rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#fff"/>
                <path d="M12 6v6l4 2" stroke="#006BFF" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-white">Calendly</h4>
              <p className="text-sm text-[#B0B8C4]">Connect your Calendly account</p>
            </div>
          </div>
          {calendarProvider === 'calendly' && (
            <Check size={20} className="text-[#00E5C0] absolute top-4 right-4" />
          )}
        </button>
        
        {/* Square Appointments */}
        <button
          onClick={() => setCalendarProvider('square')}
          className={`p-6 rounded-xl border-2 text-left transition-all ${
            calendarProvider === 'square'
              ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
              : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <rect x="4" y="4" width="16" height="16" rx="2" fill="#fff"/>
                <rect x="7" y="7" width="10" height="10" rx="1" fill="#000"/>
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-white">Square Appointments</h4>
              <p className="text-sm text-[#B0B8C4]">Connect your Square account</p>
            </div>
          </div>
          {calendarProvider === 'square' && (
            <Check size={20} className="text-[#00E5C0] absolute top-4 right-4" />
          )}
        </button>
      </div>
      
      {/* Connection Status */}
      <div className="p-6 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
        {calendarConnected ? (
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-[#00E5C0]" />
            <div>
              <h4 className="font-medium text-white">Calendar Connected</h4>
              <p className="text-sm text-[#B0B8C4]">{calendarEmail}</p>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <Calendar size={48} className="mx-auto text-[#7D8896] mb-4" />
            <h4 className="font-medium text-white mb-2">No Calendar Connected</h4>
            <p className="text-sm text-[#B0B8C4] mb-4">
              Connect your {
                calendarProvider === 'google' ? 'Google Calendar' : 
                calendarProvider === 'microsoft' ? 'Microsoft Calendar' :
                calendarProvider === 'calendly' ? 'Calendly' :
                calendarProvider === 'square' ? 'Square Appointments' : 'calendar'
              } to enable appointment booking
            </p>
            {/* OAuth Not Configured Warning - Only for Google/Microsoft */}
            {(calendarProvider === 'google' || calendarProvider === 'microsoft') && calendarOAuthConfigured === false && (
              <div className="bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.3)] rounded-lg p-4 mb-4 text-left">
                <div className="flex items-center gap-2 text-[#F59E0B] mb-2">
                  <Warning size={16} />
                  <span className="font-semibold text-sm">Admin Setup Required</span>
                </div>
                <p className="text-xs text-[#A0AEC0]">
                  {calendarProvider === 'google' ? 'Google Workspace' : 'Microsoft'} OAuth credentials must be configured by an admin in GearSix → Integrations before users can connect calendars.
                </p>
              </div>
            )}
            {/* Coming Soon for Calendly/Square */}
            {(calendarProvider === 'calendly' || calendarProvider === 'square') && (
              <div className="bg-[rgba(0,229,192,0.1)] border border-[rgba(99,102,241,0.3)] rounded-lg p-4 mb-4 text-left">
                <div className="flex items-center gap-2 text-[#00E5C0] mb-2">
                  <Calendar size={16} />
                  <span className="font-semibold text-sm">Coming Soon</span>
                </div>
                <p className="text-xs text-[#A0AEC0]">
                  {calendarProvider === 'calendly' ? 'Calendly' : 'Square Appointments'} integration is coming soon. For now, you can use Google Calendar or Microsoft Calendar.
                </p>
              </div>
            )}
            <button
              onClick={
                calendarProvider === 'google' ? connectGoogleCalendar : 
                calendarProvider === 'microsoft' ? connectMicrosoftCalendar :
                () => {} // Placeholder for Calendly/Square
              }
              disabled={
                connectingCalendar || 
                ((calendarProvider === 'google' || calendarProvider === 'microsoft') && calendarOAuthConfigured === false) ||
                calendarProvider === 'calendly' || 
                calendarProvider === 'square'
              }
              title={
                calendarOAuthConfigured === false && (calendarProvider === 'google' || calendarProvider === 'microsoft')
                  ? 'Admin setup required in Settings → Integrations' 
                  : (calendarProvider === 'calendly' || calendarProvider === 'square')
                    ? 'Coming soon'
                    : 'Connect your calendar'
              }
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg disabled:opacity-50 ${
                (calendarOAuthConfigured === false && (calendarProvider === 'google' || calendarProvider === 'microsoft')) ||
                calendarProvider === 'calendly' || 
                calendarProvider === 'square'
                  ? 'bg-gray-600 text-[#B0B8C4] cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] text-white hover:opacity-90'
              }`}
            >
              {connectingCalendar ? (
                <>
                  <CircleNotch size={18} className="animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ArrowSquareOut size={18} />
                  Connect {
                    calendarProvider === 'google' ? 'Google Calendar' : 
                    calendarProvider === 'microsoft' ? 'Microsoft Calendar' :
                    calendarProvider === 'calendly' ? 'Calendly' :
                    calendarProvider === 'square' ? 'Square' : 'Calendar'
                  }
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 7: SMS Configuration (NEW - Phase 4)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderSmsStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">SMS Configuration</h3>
        <p className="text-sm text-[#B0B8C4]">Set up SMS confirmations and reminders (requires Twilio or Telnyx)</p>
      </div>
      
      {/* SMS Enable Toggle */}
      <div className="flex items-center justify-between p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
        <div>
          <h4 className="font-medium text-white">Enable SMS Notifications</h4>
          <p className="text-sm text-[#B0B8C4]">Send SMS messages to customers</p>
        </div>
        <button
          onClick={() => setSmsEnabled(!smsEnabled)}
          className={`w-12 h-6 rounded-full transition-colors ${
            smsEnabled ? 'bg-[#00E5C0]' : 'bg-gray-600'
          }`}
        >
          <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
            smsEnabled ? 'translate-x-6' : 'translate-x-0.5'
          }`} />
        </button>
      </div>
      
      {smsEnabled && (
        <div className="space-y-6">
          {/* Confirmation SMS */}
          <div className="p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium text-white">Booking Confirmation</h4>
                <p className="text-sm text-[#B0B8C4]">Send immediately after booking</p>
              </div>
              <button
                onClick={() => setSmsConfirmationEnabled(!smsConfirmationEnabled)}
                className={`w-10 h-5 rounded-full transition-colors ${
                  smsConfirmationEnabled ? 'bg-[#00E5C0]' : 'bg-gray-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${
                  smsConfirmationEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            
            {smsConfirmationEnabled && (
              <div>
                <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                  ChatText Template
                </label>
                <textarea
                  value={smsConfirmationTemplate}
                  onChange={(e) => setSmsConfirmationTemplate(e.target.value)}
                  rows={3}
                  className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
                />
                <p className="text-xs text-[#7D8896] mt-1">
                  Variables: {'{name}'}, {'{service}'}, {'{date}'}, {'{time}'}, {'{business_name}'}
                </p>
              </div>
            )}
          </div>
          
          {/* 24-Hour Reminder SMS - Public Beta */}
          <div className="p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-medium text-white">24-Hour Appointment Reminder</h4>
                <p className="text-sm text-[#B0B8C4]">Send reminder before scheduled appointment</p>
              </div>
              <button
                onClick={() => setSmsReminderEnabled(!smsReminderEnabled)}
                className={`w-10 h-5 rounded-full transition-colors ${
                  smsReminderEnabled ? 'bg-[#00E5C0]' : 'bg-gray-600'
                }`}
              >
                <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${
                  smsReminderEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            
            {smsReminderEnabled && (
              <div className="space-y-4 pt-4 border-t border-[rgba(255,255,255,0.1)]">
                {/* Reminder Hours */}
                <div>
                  <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                    PaperPlaneRight Reminder
                  </label>
                  <select
                    value={smsReminderHours}
                    onChange={(e) => setSmsReminderHours(parseInt(e.target.value))}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
                  >
                    <option value={1}>1 hour before</option>
                    <option value={2}>2 hours before</option>
                    <option value={4}>4 hours before</option>
                    <option value={12}>12 hours before</option>
                    <option value={24}>24 hours before</option>
                    <option value={48}>48 hours before</option>
                  </select>
                </div>
                
                {/* Reminder Template */}
                <div>
                  <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                    Reminder ChatText Template
                  </label>
                  <textarea
                    value={smsReminderTemplate}
                    onChange={(e) => setSmsReminderTemplate(e.target.value)}
                    rows={3}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
                  />
                  <p className="text-xs text-[#7D8896] mt-1">
                    Variables: {'{name}'}, {'{service}'}, {'{date}'}, {'{time}'}, {'{business_name}'}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {!twilioValidated && !telnyxValidated && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-amber-400">
                <WarningCircle size={18} />
                <span>SMS requires valid Twilio or Telnyx credentials. Complete telephony setup in the next step.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 8: Voice GearSix (Phase D: True S2S Support)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderVoiceSettingsStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Voice Settings</h3>
        <p className="text-sm text-[#B0B8C4]">Configure voice conversation mode and settings</p>
      </div>
      
      {/* Realtime S2S is the only supported conversation mode */}
      
      {/* Realtime S2S GearSix (Phase D) */}
      {voiceMode === 'live_s2s' && voiceConversationMode === 'realtime_s2s' && (
        <div className="space-y-4 p-4 bg-[#00E5C0]/5 rounded-xl border border-[#00E5C0]/20">
          <h4 className="font-medium text-white flex items-center gap-2">
            <Waveform size={18} className="text-[#00E5C0]" />
            Realtime S2S Settings
          </h4>
          
          {/* Realtime Provider */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Realtime Voice Provider
            </label>
            <select
              value={realtimeProvider}
              onChange={(e) => setRealtimeProvider(e.target.value)}
              className="w-full bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
            >
              <option value="openai_realtime">OpenAI Realtime</option>
            </select>
          </div>
          
          {/* Realtime Voice */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Voice
            </label>
            <select
              value={realtimeVoice}
              onChange={(e) => setRealtimeVoice(e.target.value)}
              className="w-full bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
            >
              <option value="alloy">Alloy (Neutral)</option>
              <option value="echo">Echo (Male)</option>
              <option value="fable">Fable (British)</option>
              <option value="onyx">Onyx (Deep Male)</option>
              <option value="nova">Nova (Female)</option>
              <option value="shimmer">Shimmer (Female)</option>
            </select>
          </div>
          
          {/* Barge-in / Interruption */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-white">Barge-in / Interruption</label>
              <p className="text-xs text-[#B0B8C4]">Allow caller to interrupt the agent</p>
            </div>
            <button
              onClick={() => setBargeInEnabled(!bargeInEnabled)}
              className={`w-12 h-6 rounded-full transition-colors ${
                bargeInEnabled ? 'bg-[#00D4B0]' : 'bg-gray-600'
              }`}
            >
              <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                bargeInEnabled ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          
          {/* Silence Detection */}
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Silence Detection (seconds)
            </label>
            <input
              type="range"
              min="0.5"
              max="20"
              step="0.5"
              value={silenceTimeout}
              onChange={(e) => setSilenceTimeout(parseFloat(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-[#B0B8C4] mt-1">
              <span>0.5s (Quick)</span>
              <span className="text-[#00D4B0] font-medium">{silenceTimeout}s</span>
              <span>20s (Patient)</span>
            </div>
          </div>
        </div>
      )}
      
      {/* STT + TTS Pipeline GearSix */}
      {(voiceMode !== 'live_s2s' || voiceConversationMode === 'stt_tts') && (
        <>
          {/* STT Provider */}
          {(voiceMode === 'live_s2s' || voiceMode === 'voice_input') && (
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Speech-to-Text Provider
              </label>
              <select
                value={sttProvider}
                onChange={(e) => setSttProvider(e.target.value)}
                className="w-full bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
              >
                <option value="openai_whisper">OpenAI Whisper</option>
                <option value="deepgram">Deepgram</option>
                <option value="google">Google Cloud Speech</option>
              </select>
            </div>
          )}
          
          {/* TTS Provider */}
          {(voiceMode === 'live_s2s' || voiceMode === 'voice_output') && (
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Text-to-Speech Provider
              </label>
              <select
                value={ttsProvider}
                onChange={(e) => setTtsProvider(e.target.value)}
                className="w-full bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
              >
                <option value="openai_tts">OpenAI TTS</option>
                <option value="elevenlabs">ElevenLabs</option>
                <option value="deepgram">Deepgram</option>
              </select>
            </div>
          )}
          
          {/* Voice Selection */}
          {(voiceMode === 'live_s2s' || voiceMode === 'voice_output') && (
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Voice *
              </label>
              <select
                value={ttsVoiceId}
                onChange={(e) => setTtsVoiceId(e.target.value)}
                className="w-full bg-[rgba(20,20,30,0.8)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white"
              >
                <option value="">Select a voice</option>
                {ttsProvider === 'openai_tts' && (
                  <>
                    <option value="alloy">Alloy (Neutral)</option>
                    <option value="echo">Echo (Male)</option>
                    <option value="fable">Fable (British)</option>
                    <option value="onyx">Onyx (Deep Male)</option>
                    <option value="nova">Nova (Female)</option>
                    <option value="shimmer">Shimmer (Female)</option>
                  </>
                )}
                {ttsProvider === 'elevenlabs' && (
                  <>
                    <option value="rachel">Rachel (Female)</option>
                    <option value="drew">Drew (Male)</option>
                    <option value="clyde">Clyde (Male)</option>
                    <option value="paul">Paul (Male)</option>
                    <option value="domi">Domi (Female)</option>
                  </>
                )}
                {ttsProvider === 'deepgram' && (
                  <>
                    <option value="aura-asteria-en">Asteria (Female)</option>
                    <option value="aura-luna-en">Luna (Female)</option>
                    <option value="aura-stella-en">Stella (Female)</option>
                    <option value="aura-athena-en">Athena (Female)</option>
                    <option value="aura-hera-en">Hera (Female)</option>
                    <option value="aura-orion-en">Orion (Male)</option>
                    <option value="aura-arcas-en">Arcas (Male)</option>
                    <option value="aura-perseus-en">Perseus (Male)</option>
                    <option value="aura-angus-en">Angus (Male)</option>
                    <option value="aura-orpheus-en">Orpheus (Male)</option>
                    <option value="aura-helios-en">Helios (Male)</option>
                    <option value="aura-zeus-en">Zeus (Male)</option>
                  </>
                )}
              </select>
            </div>
          )}
        </>
      )}
      
      {/* Accent / Locale */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Accent / Locale
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {ACCENT_LOCALES.map((locale) => (
            <button
              key={locale.value}
              onClick={() => setAccentLocale(locale.value)}
              className={`p-3 rounded-lg border text-left transition-all ${
                accentLocale === locale.value
                  ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.1)]'
                  : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
              }`}
            >
              <span className="text-lg mr-2">{locale.flag}</span>
              <span className="text-sm text-white">{locale.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Max Call Duration (for all voice modes) */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
          Max Call Duration (minutes)
        </label>
        <input
          type="range"
          min="1"
          max="60"
          value={maxCallDuration}
          onChange={(e) => setMaxCallDuration(parseInt(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-[#B0B8C4] mt-1">
          <span>1 min</span>
          <span className="text-[#00E5C0] font-medium">{maxCallDuration} min</span>
          <span>60 min</span>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 9: Telephony Provider Setup (Phase E: Twilio or Telnyx)
  // ═══════════════════════════════════════════════════════════════════════════
  const renderTelephonyStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Telephony Provider</h3>
        <p className="text-sm text-[#B0B8C4]">Connect your telephony provider for voice calls and SMS</p>
      </div>
      
      {/* Provider Selection - Clean, no marketing copy */}
      <div>
        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">Provider</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setTelephonyProvider('twilio')}
            className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
              telephonyProvider === 'twilio'
                ? 'border-[#00E5C0] bg-[#00E5C0]/10'
                : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.3)]'
            }`}
          >
            <div className="w-8 h-8 rounded-lg bg-[rgba(255,107,107,0.12)] flex items-center justify-center">
              <Phone size={16} className="text-[#FF6B6B]" />
            </div>
            <span className="font-medium text-white">Twilio</span>
            {telephonyProvider === 'twilio' && twilioValidated && (
              <CheckCircle size={16} className="text-[#00E5C0] ml-auto" />
            )}
          </button>
          
          <button
            type="button"
            onClick={() => setTelephonyProvider('telnyx')}
            className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
              telephonyProvider === 'telnyx'
                ? 'border-[#00E5C0] bg-[#00E5C0]/10'
                : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.3)]'
            }`}
          >
            <div className="w-8 h-8 rounded-lg bg-[#00E5C0]/20 flex items-center justify-center">
              <Phone size={16} className="text-[#00E5C0]" />
            </div>
            <span className="font-medium text-white">Telnyx</span>
            {telephonyProvider === 'telnyx' && telnyxValidated && (
              <CheckCircle size={16} className="text-[#00E5C0] ml-auto" />
            )}
          </button>
        </div>
      </div>
      
      {/* Twilio Configuration */}
      {telephonyProvider === 'twilio' && (
        <div className="space-y-4 p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
          {/* Connected State - show masked credentials */}
          {twilioValidated && !editingTwilioCreds ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} className="text-[#00E5C0]" />
                  <h4 className="font-medium text-white">Twilio Connected</h4>
                </div>
                <button
                  onClick={() => setEditingTwilioCreds(true)}
                  className="text-xs text-[#7D8896] hover:text-[#00E5C0] transition-colors"
                >
                  Update credentials
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="px-3 py-2 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.06)]">
                  <p className="text-[10px] text-[#5C6778] uppercase tracking-wide mb-0.5">Account SID</p>
                  <p className="text-sm text-[#B0B8C4] font-mono">
                    {twilioAccountSid ? `${twilioAccountSid.slice(0, 6)}••••••••` : '••••••••••••'}
                  </p>
                </div>
                <div className="px-3 py-2 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.06)]">
                  <p className="text-[10px] text-[#5C6778] uppercase tracking-wide mb-0.5">Phone Number</p>
                  <p className="text-sm text-[#B0B8C4] font-mono">
                    {twilioPhoneNumber || '••••••••••••'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Edit State - show credential fields */
            <div className="space-y-4">
              <h4 className="font-medium text-white">Twilio Credentials</h4>
              
              <div>
                <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                  Account SID *
                </label>
                <input
                  type="text"
                  value={twilioAccountSid}
                  onChange={(e) => setTwilioAccountSid(e.target.value)}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                  Auth Token *
                  {hasExistingTwilioToken && !twilioAuthToken && (
                    <span className="ml-2 text-[#00E5C0] text-xs font-normal">
                      (Saved securely)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={twilioAuthToken}
                  onChange={(e) => {
                    setTwilioAuthToken(e.target.value);
                    if (e.target.value) setHasExistingTwilioToken(false);
                  }}
                  placeholder={hasExistingTwilioToken ? "Enter new token to replace existing" : "Your auth token"}
                  className={`w-full bg-[rgba(255,255,255,0.05)] border rounded-lg px-4 py-2 text-white placeholder-gray-500 ${
                    hasExistingTwilioToken && !twilioAuthToken 
                      ? 'border-[#00E5C0]/30' 
                      : 'border-[rgba(255,255,255,0.1)]'
                  }`}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                  Phone Number *
                </label>
                <input
                  type="text"
                  value={twilioPhoneNumber}
                  onChange={(e) => setTwilioPhoneNumber(e.target.value)}
                  placeholder="+1xxxxxxxxxx"
                  className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
                />
              </div>
              
              <button
                onClick={validateTwilio}
                disabled={validatingTwilio || !twilioAccountSid || (!twilioAuthToken && !hasExistingTwilioToken)}
                className="flex items-center gap-2 px-6 py-2 bg-[#00E5C0] text-[#0B1120] rounded-lg hover:opacity-90 disabled:opacity-50 font-medium text-sm"
              >
                {validatingTwilio ? (
                  <>
                    <CircleNotch size={18} className="animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Validate Credentials
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Telnyx Configuration */}
      {telephonyProvider === 'telnyx' && (
        <div className="space-y-4 p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
          <h4 className="font-medium text-white">Telnyx Credentials</h4>
          
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              API Key *
              {hasExistingTelnyxKey && !telnyxApiKey && (
                <span className="ml-2 text-[#00E5C0] text-xs font-normal">
                  (Saved: ••••••••••••)
                </span>
              )}
            </label>
            <input
              type="password"
              value={telnyxApiKey}
              onChange={(e) => {
                setTelnyxApiKey(e.target.value);
                if (e.target.value) setHasExistingTelnyxKey(false);
              }}
              placeholder={hasExistingTelnyxKey ? "Enter new key to replace existing" : "KEY_xxxxxxxxxxxxxxxx"}
              className={`w-full bg-[rgba(255,255,255,0.05)] border rounded-lg px-4 py-2 text-white placeholder-gray-500 ${
                hasExistingTelnyxKey && !telnyxApiKey 
                  ? 'border-green-500/30' 
                  : 'border-[rgba(255,255,255,0.1)]'
              }`}
            />
            {hasExistingTelnyxKey && !telnyxApiKey && (
              <p className="text-xs text-[#7D8896] mt-1">
                API key is securely stored. Leave empty to keep existing, or enter new key to replace.
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Phone Number *
            </label>
            <input
              type="text"
              value={telnyxPhoneNumber}
              onChange={(e) => setTelnyxPhoneNumber(e.target.value)}
              placeholder="+1xxxxxxxxxx"
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-2 text-white placeholder-gray-500"
            />
          </div>
          
          <button
            onClick={validateTelnyx}
            disabled={validatingTelnyx || (!telnyxApiKey && !hasExistingTelnyxKey) || !telnyxPhoneNumber}
            className="flex items-center gap-2 px-6 py-2 bg-[#00E5C0] text-white rounded-lg hover:bg-[#5558E3] disabled:opacity-50"
          >
            {validatingTelnyx ? (
              <>
                <CircleNotch size={18} className="animate-spin" />
                Validating...
              </>
            ) : telnyxValidated ? (
              <>
                <CheckCircle size={18} />
                Validated
              </>
            ) : (
              <>
                <Check size={18} />
                Validate Credentials
              </>
            )}
          </button>
        </div>
      )}
      
      {/* WebhooksLogo URLs (show after validation) */}
      {(twilioValidated || telnyxValidated) && webhookUrls && (
        <div className="p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
          <h4 className="font-medium text-white mb-4">WebhooksLogo Configuration</h4>
          <p className="text-sm text-[#B0B8C4] mb-4">
            Configure these webhooks in your Twilio phone number settings:
          </p>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-[#B0B8C4] mb-1">Voice WebhooksLogo URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-black/30 rounded text-sm text-[#00E5C0] overflow-x-auto">
                  {webhookUrls.voice_url}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(webhookUrls.voice_url);
                    setCopiedUrl('voice');
                    setTimeout(() => setCopiedUrl(null), 2000);
                  }}
                  className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded"
                >
                  {copiedUrl === 'voice' ? <CheckCircle size={18} className="text-[#00E5C0]" /> : <Copy size={18} className="text-[#B0B8C4]" />}
                </button>
              </div>
            </div>
            
            {smsEnabled && (
              <div>
                <label className="block text-sm text-[#B0B8C4] mb-1">SMS WebhooksLogo URL</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-black/30 rounded text-sm text-[#00E5C0] overflow-x-auto">
                    {webhookUrls.sms_url}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(webhookUrls.sms_url);
                      setCopiedUrl('sms');
                      setTimeout(() => setCopiedUrl(null), 2000);
                    }}
                    className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded"
                  >
                    {copiedUrl === 'sms' ? <CheckCircle size={18} className="text-[#00E5C0]" /> : <Copy size={18} className="text-[#B0B8C4]" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 10: Review & Test
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Check if telephony is configured for live voice agents
  const isTelephonyConfigured = telephonyProvider === 'twilio' ? twilioValidated : telnyxValidated;
  const needsTelephonyWarning = voiceMode === 'live_s2s' && !isTelephonyConfigured;
  
  const renderReviewStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">Review & Test</h3>
        <p className="text-sm text-[#B0B8C4]">Review your configuration and test your voice agent</p>
      </div>
      
      {/* Non-blocking Telephony Warning for Live Voice Agents */}
      {needsTelephonyWarning && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-start gap-3">
            <WarningCircle size={24} className="text-amber-400 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-amber-400">Telephony Provider Required for Activation</h4>
              <p className="text-sm text-amber-400/80 mt-1">
                This voice agent requires a telephony provider (Twilio or Telnyx) to be connected before it can be activated.
                You can complete the wizard now and configure telephony later in the Advanced Editor.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Configuration Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
          <h4 className="font-medium text-white mb-3">Voice Configuration</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#B0B8C4]">Mode:</span>
              <span className="text-white">{VOICE_MODES.find(m => m.id === voiceMode)?.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#B0B8C4]">Persona:</span>
              <span className="text-white">{personaName || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#B0B8C4]">Voice:</span>
              <span className="text-white">{ttsVoiceId || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#B0B8C4]">Locale:</span>
              <span className="text-white">{ACCENT_LOCALES.find(l => l.value === accentLocale)?.label}</span>
            </div>
          </div>
        </div>
        
        <div className="p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
          <h4 className="font-medium text-white mb-3">Integrations</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#B0B8C4]">Telephony ({telephonyProvider === 'twilio' ? 'Twilio' : 'Telnyx'}):</span>
              <span className={isTelephonyConfigured ? 'text-[#00E5C0]' : 'text-amber-400'}>
                {isTelephonyConfigured ? 'Connected' : 'Not Configured'}
              </span>
            </div>
            {isBookingAgent && (
              <div className="flex justify-between">
                <span className="text-[#B0B8C4]">Calendar:</span>
                <span className={calendarConnected ? 'text-[#00E5C0]' : 'text-amber-400'}>
                  {calendarConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[#B0B8C4]">SMS Confirmation:</span>
              <span className={smsEnabled && smsConfirmationEnabled ? 'text-[#00E5C0]' : 'text-[#B0B8C4]'}>
                {smsEnabled && smsConfirmationEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#B0B8C4]">SMS Reminder:</span>
              <span className={smsEnabled && smsReminderEnabled ? 'text-[#00E5C0]' : 'text-[#B0B8C4]'}>
                {smsEnabled && smsReminderEnabled ? `${smsReminderHours}h before` : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Test Voice */}
      {voiceMode === 'live_s2s' && twilioValidated && (
        <div className="p-4 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.1)]">
          <h4 className="font-medium text-white mb-3">Test Your Voice Agent</h4>
          <button
            onClick={() => setShowVoiceTest(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] text-white rounded-lg hover:opacity-90"
          >
            <Microphone size={18} />
            Start Voice Test
          </button>
        </div>
      )}
      
      {/* Completion ChatText */}
      <div className="p-4 bg-[#00E5C0]/10 border border-green-500/30 rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle size={24} className="text-[#00E5C0]" />
          <div>
            <h4 className="font-medium text-[#00E5C0]">Configuration Complete</h4>
            <p className="text-sm text-[#00E5C0]/80 mt-1">
              Click &quot;Finish&quot; to save your configuration and return to the agent page.
              You can always come back to edit settings in the Advanced Editor.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className={`${isModal ? 'fixed inset-4 z-50 rounded-2xl border border-[rgba(255,255,255,0.08)]' : 'min-h-screen'} bg-[#1F2937] flex items-center justify-center`}>
        <div className="text-center">
          <CircleNotch size={48} className="animate-spin text-[#00E5C0] mx-auto mb-4" />
          <p className="text-[#B0B8C4]">Loading wizard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    {isModal && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => handleCancel()} />
    )}
    <div className={`${isModal ? 'fixed inset-4 z-50 rounded-2xl border border-[rgba(255,255,255,0.08)] shadow-2xl overflow-auto' : 'min-h-screen'} bg-[#1F2937] py-8`}>
      <div className="max-w-4xl mx-auto px-6">
        {/* Header with Cancel Button */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#F0F4F8] mb-2">
              Voice Agent Setup
            </h1>
            <p className="text-[#B0B8C4]">
              {agent?.name || 'Configure your voice agent'} — Step {currentStep + 1} of {steps.length}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 text-[#7D8896] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.06)] rounded-lg transition-colors"
            title="Cancel"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {steps.map((step, idx) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => idx < currentStep && setCurrentStep(idx)}
                disabled={idx > currentStep}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  idx === currentStep
                    ? 'bg-[#00E5C0] text-[#12161C] font-semibold'
                    : idx < currentStep
                    ? 'bg-[rgba(0,229,192,0.12)] text-[#00E5C0] cursor-pointer hover:bg-[rgba(0,229,192,0.2)]'
                    : 'bg-[rgba(255,255,255,0.04)] text-[#7D8896] cursor-not-allowed'
                }`}
              >
                {idx < currentStep ? (
                  <Check size={16} />
                ) : (
                  <step.icon size={16} />
                )}
                <span className="hidden sm:inline">{step.title}</span>
              </button>
              {idx < steps.length - 1 && (
                <CaretRight size={16} className="text-[#2A3441] flex-shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-[rgba(255,107,107,0.08)] border border-[rgba(255,107,107,0.2)] rounded-xl flex items-center gap-3">
            <WarningCircle size={20} className="text-[#FF6B6B] flex-shrink-0" />
            <span className="text-[#FF6B6B] flex-1">{error}</span>
            {error.includes('unavailable') || error.includes('try again') ? (
              <button 
                onClick={() => { setError(null); nextStep(); }}
                disabled={saving || advancing}
                className="px-4 py-1.5 bg-[rgba(255,107,107,0.12)] text-[#FF6B6B] hover:bg-[#FF6B6B]/30 rounded-lg text-sm font-medium transition-colors"
              >
                Retry
              </button>
            ) : null}
            <button onClick={() => setError(null)} className="text-[#FF6B6B] hover:text-red-300 text-xl font-light">
              ×
            </button>
          </div>
        )}
        
        {/* Step Content */}
        <div className="bg-[#111827] rounded-2xl border border-[rgba(255,255,255,0.08)] p-8 mb-8">
          {renderStepContent()}
        </div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-3 border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] rounded-xl hover:bg-[rgba(255,255,255,0.04)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CaretLeft size={18} />
              Back
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            {saving && (
              <span className="text-[#B0B8C4] flex items-center gap-2">
                <CircleNotch size={16} className="animate-spin text-[#00E5C0]" />
                Saving...
              </span>
            )}
            
            {currentStep < steps.length - 1 ? (
              <button
                onClick={nextStep}
                disabled={saving || advancing}
                className="flex items-center gap-2 px-6 py-3 bg-[#00E5C0] text-[#12161C] font-semibold rounded-xl hover:bg-[#00CCB0] disabled:opacity-50"
              >
                {advancing ? 'Advancing...' : 'Continue'}
                <CaretRight size={18} />
              </button>
            ) : (
              <button
                onClick={finishWizard}
                disabled={saving || advancing}
                className="flex items-center gap-2 px-6 py-3 bg-[#00E5C0] text-[#12161C] font-semibold rounded-xl hover:bg-[#00CCB0] disabled:opacity-50"
              >
                <Check size={18} />
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Voice Test Modal */}
      {showVoiceTest && (
        <VoiceTestMode
          agentId={agentId}
          agentName={businessName}
          businessName={businessName}
          businessDescription={businessDescription}
          customKnowledge={customKnowledge}
          openingMessage={greetingMessage}
          requiredBookingInfo={requiredBookingInfo}
          onClose={() => setShowVoiceTest(false)}
        />
      )}
      
      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-[#111827] rounded-2xl border border-[rgba(255,255,255,0.08)] p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-[#F0F4F8] mb-2">Discard changes?</h3>
            <p className="text-[#B0B8C4] mb-6">
              Are you sure you want to exit the wizard? Any unsaved changes will be lost.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 text-[#B0B8C4] hover:text-[#F0F4F8] transition-colors"
              >
                Continue Editing
              </button>
              <button
                onClick={confirmCancel}
                className="px-4 py-2 bg-[#FF6B6B] text-white rounded-xl hover:bg-[#E85555] transition-colors"
              >
                Discard & Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default UniversalVoiceWizard;
